import React, { useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Avatar,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Skeleton,
  InputAdornment,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Send as SendIcon,
  Add as AddIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  TableChart as TableIcon,
  BarChart as ChartIcon,
  Download as DownloadIcon,
  InsertDriveFile as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import './ChatBot.css';
import GenAIConfig from '../../assets/GenAIConfig.json';
import { TERMS, replaceQuotaTerm } from '../../config/terminology';
import QuestionPromptCard from './QuestionPromptCard';
import DashboardCard from './DashboardCard';
import MiniChartCard from './MiniChartCard';
import MiniKPICard from './MiniKPICard';
import UnconstrainedForecastChart from './UnconstrainedForecastChart';
import ChartRenderer from './ChartRenderer';
import TableRenderer from './TableRenderer';
import { chatbotUrls } from './chatbotUtils';
import {
  startConversation,
  continueChat,
  getFollowupQuestions,
  getDashboardCards,
  getMostAskedQuestions,
  getFrequentlyAskedQuestions,
  getConversationHistory,
  addUserMessage,
  addErrorMessage,
  clearMessages,
  setCurrentChat,
  addNewChat,
  updateChatTitle,
  deleteChat,
  clearError,
  clearFollowupQuestions,
  getUserChatBotHistory
} from './chatbotSlice';
import { useNavigate } from 'react-router-dom';
import { setState as setAnalyzeState } from '../Analyze/analyzeSlice';
import { setState as setLoginState } from '../../store/loginSlice';

const DRAWER_WIDTH = 260;
const MAX_CONVERSATION_MESSAGES = 20; // Maximum number of user messages per conversation
const MAX_INPUT_CHARACTERS = 1000; // Maximum characters in input field
const HIDE_DATA_EXPLORATION_AGENT =
  import.meta.env.VITE_APP_HIDE_DATA_EXPLORATION_AGENT === 'true';

/** Matches API ChatBot:DemoDataQueryMessage — hide data-output disclaimer for this reply */
const DEMO_DATA_QUERY_SNIPPET =
  'not been provisionned access to the relevant data in this demo environment';

const shouldShowOutputVerificationDisclaimer = (description) =>
  !description?.includes(DEMO_DATA_QUERY_SNIPPET);

const outputVerificationDisclaimerSx = {
  mb: 1,
  fontSize: '14px',
  lineHeight: 1.6,
  color: '#303030',
};

const OutputVerificationDisclaimer = () => (
  <Typography variant="body2" sx={outputVerificationDisclaimerSx}>
    Verify the correctness of all outputs before relying on them.
  </Typography>
);

// Feature Flags - Toggle features on/off
const FEATURE_FLAGS = {
  SHOW_FAQS_ON_WELCOME: false,  // Set to true to show FAQs on welcome screen
  SHOW_FORECAST_ON_WELCOME: true, // Set to true to show forecast chart on welcome screen
  DISPLAY_DRIVER_ALERTS: true, // Set to false to hide driver alerts row
};

// IQVIA Brand Colors for Charts
const COLORS = ['#005587', '#00BFB3', '#43B02A', '#00A3E0', '#008F86', '#027223', '#FE8A12'];

// Fallback used when /kpi-config.json cannot be fetched
const KPI_SUMMARY_CARDS_DEFAULT = [
  { key: 'unconstrainedDemand', title: 'Unconstrained Demand', value: '1.28M', subtitle: '18-month forecast',   color: '#005587' },
  { key: 'constrainedForecast', title: 'Driver adjusted forecast', value: '1.12M', subtitle: 'after supply limits', color: '#00BFB3' },
  { key: 'demandSupplyGap',     title: 'Demand-Supply Gap',    value: '160k',  subtitle: '13% unmet demand',    color: '#FE8A12' },
  { key: 'productsAtRisk',      title: 'Products at Risk',     value: '5',     subtitle: 'needs review',        color: '#d32f2f' },
  { key: 'signalsDetected',     title: 'Signals Detected',     value: '24',    subtitle: '6 high impact',       color: '#43B02A' },
  { key: 'scenariosPending',    title: 'Scenarios Pending',    value: '3',     subtitle: 'awaiting consensus',  color: '#00A3E0' },
];

// KPI Dashboard Card Titles and Config (data comes from API)
// Note: Titles use TERMS for dynamic terminology (Quota -> Res. Allocation)
const getKpiCardConfig = () => ({
  adherenceToProductObjectives: {
    title: "Detected Signals by Category",
    chartType: "donut",
    iconType: "adherence",
    nameKey: "name",
    valueKey: "demandVsSupply",
  },
  quotasAtRisk: {
    title: `Products at Supply Risk`,
    chartType: "verticalBar",
    iconType: "risk",
    nameKey: "name",
    valueKey: "value",
  },
  quotasPendingSubmission: {
    title: `Number of ${TERMS.QUOTA_PLURAL} Pending Submission`,
    chartType: "horizontalBar",
    iconType: "submission",
    nameKey: "name",
    valueKey: "value",
  },
  quotasPendingApproval: {
    title: `Forecast build-up by driver`,
    chartType: "waterfall",
    iconType: "approval",
    nameKey: "name",
    valueKey: "value",
  },
  topCountriesHighestDemand: {
    title: "Top Markets with Demand-Supply Gap",
    chartType: "horizontalBar",
    iconType: "emergency",
    nameKey: "name",
    valueKey: "value",
    unit: "UNITS",
  },
  topCountriesMostExcess: {
    title: "Top 5 Countries with Most Excess",
    chartType: "horizontalBar",
    iconType: "excess",
    nameKey: "name",
    valueKey: "value",
    unit: "UNITS",
  },
  topQuotaBreachesVsInitial: {
    title: `Top 5 ${TERMS.QUOTA} Breaches vs Initial ${TERMS.QUOTA}`,
    chartType: "horizontalBar",
    iconType: "breach",
    nameKey: "name",
    valueKey: "value",
  },
  topProductExcessGrowth: {
    title: "Top 5 Product Excess Growth",
    chartType: "verticalBar",
    iconType: "growth",
    nameKey: "name",
    valueKey: "value",
  }
});

// Get the config (called once, uses current terminology setting)
const KPI_CARD_CONFIG = getKpiCardConfig();

const DEFAULT_CONSTRAINED_FACTOR = -7;

/** Deterministic hash from a string — used to simulate brand-specific chart values */
const hashProductSeed = (product) => {
  let hash = 0;
  const text = String(product || '');
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

/** Slightly vary a numeric value based on product seed (keeps sign, ~±amplitude) */
const varyNumericValue = (rawValue, seed, index, amplitude = 0.18) => {
  const numeric = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue).replace(/,/g, ''));
  if (Number.isNaN(numeric)) return rawValue;
  const unit = ((seed + index * 31) % 100) / 100; // 0..0.99
  const factor = 1 + (unit - 0.5) * 2 * amplitude;
  const varied = Math.round(numeric * factor);
  // Preserve original string formatting for signed waterfall values (e.g. "+250")
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (trimmed.startsWith('+') && varied >= 0) return `+${varied}`;
    if (trimmed.startsWith('+') && varied < 0) return `${varied}`;
    if (trimmed.startsWith('-')) return `${varied}`;
    return String(varied);
  }
  return varied;
};

/** Vary Detected Signals by Category donut data for the selected brand */
const varySignalsByCategory = (rows, product) => {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const seed = hashProductSeed(product);
  return rows.map((row, index) => ({
    ...row,
    demandVsSupply: varyNumericValue(row.demandVsSupply ?? row.value, seed, index, 0.35),
    targetVsSupply: row.targetVsSupply,
  }));
};

/** Vary Forecast build-up waterfall data for the selected brand */
const varyForecastBuildUp = (rows, product) => {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  const seed = hashProductSeed(product);
  let running = 0;
  return rows.map((row, index) => {
    const variedValue = varyNumericValue(row.value, seed, index, 0.2);
    const numeric = typeof variedValue === 'number' ? variedValue : parseFloat(String(variedValue).replace(/,/g, ''));
    running += Number.isNaN(numeric) ? 0 : numeric;
    return {
      ...row,
      value: variedValue,
      runningTotal: String(running),
    };
  });
};

/** Parse free-text constrained factor (%); falls back to default when empty/invalid */
const parseConstrainedFactor = (raw) => {
  if (raw == null || String(raw).trim() === '') return DEFAULT_CONSTRAINED_FACTOR;
  const numeric = Number(String(raw).trim().replace(/%/g, ''));
  return Number.isFinite(numeric) ? numeric : DEFAULT_CONSTRAINED_FACTOR;
};

/** Allow only numeric factor input: optional leading "-", digits, optional single decimal */
const isValidConstrainedFactorInput = (value) =>
  value === '' || /^-?\d*\.?\d*$/.test(value);

const formatKpiUnits = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(2)}M`.replace(/\.00M$/, 'M').replace(/(\.\d)0M$/, '$1M');
  }
  if (Math.abs(n) >= 1_000) {
    return `${(n / 1_000).toFixed(1)}k`.replace(/\.0k$/, 'k');
  }
  return `${Math.round(n)}`;
};

const getCountryOptions = (forecastBySku) =>
  Object.keys(forecastBySku?.countries || {});

const getBrandOptions = (forecastBySku, country) =>
  Object.keys(forecastBySku?.countries?.[country]?.brands || {});

const getSkuOptions = (forecastBySku, country, brand) =>
  (forecastBySku?.countries?.[country]?.brands?.[brand]?.skus || []).map((s) => s.description);

const getSkuPoints = (forecastBySku, country, brand, skuDescription) => {
  const skus = forecastBySku?.countries?.[country]?.brands?.[brand]?.skus || [];
  return skus.find((s) => s.description === skuDescription)?.points || [];
};

/**
 * Build chart payload from SKU monthly points.
 * Actual → Historical Actuals; Forecast → Unconstrained; Constrained = Unconstrained × (1 + factor%).
 */
const buildForecastChartFromSku = (points, factorPercent) => {
  if (!Array.isArray(points) || points.length === 0) {
    return { dates: [], series: [], presentIso: null, lastIso: null };
  }

  const multiplier = 1 + parseConstrainedFactor(factorPercent) / 100;
  const dates = points.map((p) => p.month);
  const historical = points.map((p) => ({
    x: p.month,
    y: p.period === 'Actual' && p.actualDemand != null ? Math.round(Number(p.actualDemand)) : null,
  }));
  const unconstrained = points.map((p) => ({
    x: p.month,
    y: p.period === 'Forecast' && p.unconstrainedForecast != null
      ? Math.round(Number(p.unconstrainedForecast))
      : null,
  }));
  const constrained = unconstrained.map((point) => ({
    x: point.x,
    y: point.y == null ? null : Math.max(0, Math.round(point.y * multiplier)),
  }));

  const actualMonths = points.filter((p) => p.period === 'Actual').map((p) => p.month);
  const presentIso = actualMonths.length > 0 ? actualMonths[actualMonths.length - 1] : dates[0];
  const lastIso = dates[dates.length - 1];

  return {
    dates,
    startDate: dates[0],
    endDate: lastIso,
    presentIso,
    lastIso,
    series: [
      { name: 'Historical Actuals', data: historical },
      { name: 'Driver adjusted forecast', data: constrained },
      { name: 'Unconstrained Demand', data: unconstrained },
    ],
  };
};

/** Sum forecast-period unconstrained / constrained totals for KPI cards */
const computeForecastKpiTotals = (points, factorPercent) => {
  const multiplier = 1 + parseConstrainedFactor(factorPercent) / 100;
  const forecastPoints = (points || []).filter((p) => p.period === 'Forecast' && p.unconstrainedForecast != null);
  const unconstrained = forecastPoints.reduce((sum, p) => sum + Number(p.unconstrainedForecast), 0);
  const constrained = Math.max(0, unconstrained * multiplier);
  return {
    unconstrained,
    constrained,
    forecastMonths: forecastPoints.length,
  };
};

// First-row KPI cards: add/remove items here to enable/disable a KPI on row 1.
const FIRST_ROW_KPI_CARDS = [
  {
    id: 'adherenceToProductObjectives',
    showPercentage: false,
    minHeightOverride: { xs: '108px', sm: '120px', md: '132px' },
    isDemoData: true,
  },
  {
    id: 'quotasAtRisk',
    showPercentage: false,
    minHeightOverride: { xs: '108px', sm: '120px', md: '132px' },
    isDemoData: true,
  },
  {
    id: 'quotasPendingApproval',
    unit: '(Units)',
    showPercentage: false,
    minHeightOverride: { xs: '108px', sm: '120px', md: '132px' },
    isDemoData: true,
  },
];

/** KPI summary cards that use illustrative/demo values (asterisk + footnote) */
const DEMO_DATA_KPI_KEYS = new Set([
  'demandSupplyGap',
  'productsAtRisk',
  'signalsDetected',
  'scenariosPending',
]);

const DEMO_DATA_FOOTNOTE =
  '* Illustrative / dummy data - not live system values';

const ALERT_DETAIL_COLUMNS = [
  'notice_id',
  'source_system',
  'event_title',
  'product_title',
  'modality',
  'brand_name',
  'active_ingredient',
  'supplier_name',
  'buyer_name',
  'buyer_country',
  'award_url',
];

const MARKET_DRIVER_COLUMNS = [
  'notice_id',
  'source_system',
  'event_title',
  'product_title',
  'modality',
  'brand_name',
  'active_ingredient',
  'supplier_name',
  'manufacturer_name',
  'buyer_name',
  'buyer_country',
  'contract_start_date',
  'contract_end_date',
  'award_url',
];

const DEMO_ALERT_CARDS = [
  {
    key: 'commercial-drivers',
    title: 'Commercial Drivers',
    subtitle: 'New Signals Detected',
    summary: '5 new commercial signals detected from external tender activity and contract award changes across strategic markets.',
    columns: ALERT_DETAIL_COLUMNS,
    details: [
      {
        notice_id: 'NTC-2026-001',
        source_system: 'TenderStream',
        event_title: 'Hospital Framework Tender Awarded',
        product_title: 'Neurolexa 10mg Tablet',
        modality: 'Oral Solid',
        brand_name: 'NEUROLEXA',
        active_ingredient: 'Amlodipine Besylate',
        supplier_name: 'IQVIA Demo Supplier A',
        buyer_name: 'Milan Regional Hospital Group',
        buyer_country: 'Italy',
        award_url: 'https://demo.sims/awards/NTC-2026-001',
      },
      {
        notice_id: 'NTC-2026-002',
        source_system: 'TenderStream',
        event_title: 'Public Oncology Contract Extension',
        product_title: 'Cardiolin XR 25mg',
        modality: 'Extended Release Oral',
        brand_name: 'CARDIOLIN',
        active_ingredient: 'Benazepril Hydrochloride',
        supplier_name: 'IQVIA Demo Supplier B',
        buyer_name: 'Catalonia Health Service',
        buyer_country: 'Spain',
        award_url: 'https://demo.sims/awards/NTC-2026-002',
      },
      {
        notice_id: 'NTC-2026-003',
        source_system: 'ContractPulse',
        event_title: 'National Multi-year Procurement Closed',
        product_title: 'Aldorva 5mg Capsule',
        modality: 'Capsule',
        brand_name: 'ALDORVA',
        active_ingredient: 'Omeprazole',
        supplier_name: 'IQVIA Demo Supplier C',
        buyer_name: 'NHS England',
        buyer_country: 'United Kingdom',
        award_url: 'https://demo.sims/awards/NTC-2026-003',
      },
    ],
  },
  {
    key: 'market-drivers',
    title: 'Market Drivers',
    subtitle: 'New Signals Detected',
    summary: 'The table below illustrates a set of recent procurement events across CT, MRI, ultrasound and nuclear medicine, highlighting competitive market activity.',
    columns: MARKET_DRIVER_COLUMNS,
    details: [
      {
        notice_id: 'LPP-2024-001',
        source_system: 'NHS',
        event_title: 'Contrast Media Framework',
        product_title: 'Contrast Media',
        modality: 'CT',
        brand_name: 'NULL',
        active_ingredient: 'NULL',
        supplier_name: 'BRACCO UK LTD',
        manufacturer_name: 'BRACCO SPA',
        buyer_name: 'London Procurement Partnership',
        buyer_country: 'GB',
        contract_start_date: '2025-07-01',
        contract_end_date: '2027-06-30',
        award_url: 'https://www.commercialsolutions-sec.nhs.uk/frameworks/contrast-media-and-associated-injectors-consumables',
      },
      {
        notice_id: 'LPP-2024-001',
        source_system: 'NHS',
        event_title: 'Contrast Media Framework',
        product_title: 'Contrast Media',
        modality: 'CT',
        brand_name: 'NULL',
        active_ingredient: 'NULL',
        supplier_name: 'BAYER PLC',
        manufacturer_name: 'BAYER',
        buyer_name: 'London Procurement Partnership',
        buyer_country: 'GB',
        contract_start_date: '2025-07-01',
        contract_end_date: '2027-06-30',
        award_url: 'NULL',
      },
      {
        notice_id: 'LPP-2024-001',
        source_system: 'NHS',
        event_title: 'Contrast Media Framework',
        product_title: 'Contrast Media',
        modality: 'CT',
        brand_name: 'NULL',
        active_ingredient: 'NULL',
        supplier_name: 'GE HEALTHCARE PHARMACEUTICAL DIAGNOSTICS',
        manufacturer_name: 'GE HEALTHCARE',
        buyer_name: 'London Procurement Partnership',
        buyer_country: 'GB',
        contract_start_date: '2025-07-01',
        contract_end_date: '2027-06-30',
        award_url: 'NULL',
      },
      {
        notice_id: 'LPP-2024-001',
        source_system: 'NHS',
        event_title: 'Contrast Media Framework',
        product_title: 'Contrast Media',
        modality: 'CT',
        brand_name: 'NULL',
        active_ingredient: 'NULL',
        supplier_name: 'GUERBET LABORATORIES LTD',
        manufacturer_name: 'GUERBET',
        buyer_name: 'London Procurement Partnership',
        buyer_country: 'GB',
        contract_start_date: '2025-07-01',
        contract_end_date: '2027-06-30',
        award_url: 'NULL',
      },
      {
        notice_id: '36C24125F0032',
        source_system: 'USAspending',
        event_title: 'Rubidium Generators',
        product_title: 'Rubidium Generators',
        modality: 'PET',
        brand_name: 'NULL',
        active_ingredient: 'Rubidium RB-82',
        supplier_name: 'BRACCO DIAGNOSTICS INC.',
        manufacturer_name: 'BRACCO SPA',
        buyer_name: 'Department of Veterans Affairs',
        buyer_country: 'US',
        contract_start_date: '2025-01-24',
        contract_end_date: '2025-10-23',
        award_url: 'https://www.usaspending.gov/award/CONT_AWD_36C24125F0032_3600_36F79718D0501_3600',
      },
      {
        notice_id: '36C25624D0045',
        source_system: 'USAspending',
        event_title: 'PET Rubidium RB82 System',
        product_title: 'PET Rubidium RB82 System',
        modality: 'PET',
        brand_name: 'NULL',
        active_ingredient: 'Rubidium RB-82',
        supplier_name: 'BRACCO DIAGNOSTICS INC.',
        manufacturer_name: 'BRACCO SPA',
        buyer_name: 'Department of Veterans Affairs',
        buyer_country: 'US',
        contract_start_date: '2024-03-11',
        contract_end_date: '2029-03-10',
        award_url: 'https://www.usaspending.gov/award/CONT_IDV_36C25624D0045_3600',
      },
      {
        notice_id: '36C26324F0003',
        source_system: 'USAspending',
        event_title: 'Rubidium RB82 Generator Contract',
        product_title: 'Rubidium RB-82 Generator',
        modality: 'PET',
        brand_name: 'NULL',
        active_ingredient: 'Rubidium RB-82',
        supplier_name: 'BRACCO DIAGNOSTICS INC.',
        manufacturer_name: 'BRACCO SPA',
        buyer_name: 'Department of Veterans Affairs',
        buyer_country: 'US',
        contract_start_date: '2023-10-01',
        contract_end_date: '2024-09-30',
        award_url: 'https://www.usaspending.gov/award/CONT_AWD_36C26324F0003_3600_36F79718D0501_3600',
      },
      {
        notice_id: 'N0025922F0026',
        source_system: 'USAspending',
        event_title: 'Lumason Procurement',
        product_title: 'Contrast Agent',
        modality: 'Ultrasound',
        brand_name: 'Lumason',
        active_ingredient: 'Sulfur Hexafluoride',
        supplier_name: 'BRACCO DIAGNOSTICS INC.',
        manufacturer_name: 'BRACCO SPA',
        buyer_name: 'Department of Defense',
        buyer_country: 'US',
        contract_start_date: '2021-12-15',
        contract_end_date: '2022-06-15',
        award_url: 'https://www.usaspending.gov/award/CONT_AWD_N0025922F0026_9700_36F79718D0501_3600',
      },
      {
        notice_id: 'JN-36-25-L1',
        source_system: 'Hospital Tender',
        event_title: 'CT/MRI Contrast Media',
        product_title: 'Gadobutrol 15ml',
        modality: 'MRI',
        brand_name: 'NULL',
        active_ingredient: 'Gadobutrol',
        supplier_name: 'TUZLA-FARM',
        manufacturer_name: 'NULL',
        buyer_name: 'Prim dr Abdulah Nakas Hospital',
        buyer_country: 'BA',
        contract_start_date: 'NULL',
        contract_end_date: 'NULL',
        award_url: 'NULL',
      },
      {
        notice_id: 'JN-36-25-L2',
        source_system: 'Hospital Tender',
        event_title: 'CT Contrast Media',
        product_title: 'Iohexol 50ml',
        modality: 'CT',
        brand_name: 'NULL',
        active_ingredient: 'Iohexol',
        supplier_name: 'FARMACIJA 2011',
        manufacturer_name: 'NULL',
        buyer_name: 'Prim dr Abdulah Nakas Hospital',
        buyer_country: 'BA',
        contract_start_date: 'NULL',
        contract_end_date: 'NULL',
        award_url: 'NULL',
      },
    ],
  },
  {
    key: 'healthcare-demand-drivers',
    title: 'Healthcare / Demand Drivers',
    subtitle: 'New Signals Detected',
    summary: '6 healthcare utilization and epidemiology signals detected with potential near-term demand impact.',
    details: [],
  },
];

const ALERT_CARDS_DEFAULT = DEMO_ALERT_CARDS;

const ChatBubbleIcon = () => (
  <svg
    width="60%"
    height="60%"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M20.9996 11.5C20.9996 16.1944 17.194 20 12.4996 20C11.4228 20 10.3928 19.7998 9.44478 19.4345C9.27145 19.3678 9.18478 19.3344 9.11586 19.3185C9.04807 19.3029 8.999 19.2963 8.92949 19.2937C8.85881 19.291 8.78127 19.299 8.62619 19.315L3.50517 19.8444C3.01692 19.8948 2.7728 19.9201 2.6288 19.8322C2.50337 19.7557 2.41794 19.6279 2.3952 19.4828C2.36909 19.3161 2.48575 19.1002 2.71906 18.6684L4.35472 15.6408C4.48942 15.3915 4.55677 15.2668 4.58728 15.1469C4.6174 15.0286 4.62469 14.9432 4.61505 14.8214C4.60529 14.6981 4.55119 14.5376 4.443 14.2166C4.15547 13.3636 3.99962 12.45 3.99962 11.5C3.99962 6.80558 7.8052 3 12.4996 3C17.194 3 20.9996 6.80558 20.9996 11.5Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Memoized Message Item Component - prevents re-render when typing in input
const MessageItem = memo(({ message, renderFormattedDescription, renderResult }) => {
  return (
    <Box
      className={`message-wrapper ${message.sender}`}
      sx={{
        display: 'flex',
        justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
        mb: 3,
        alignItems: 'flex-start',
      }}
    >
      {message.sender === 'bot' && (
        <Avatar
          sx={{
            bgcolor: message.isError ? '#d32f2f' : '#00A3E0',
            width: 36,
            height: 36,
            mr: 2,
            mt: 0.5,
          }}
        >
          <BotIcon sx={{ fontSize: 20 }} />
        </Avatar>
      )}
      <Box sx={{ maxWidth: message.sender === 'user' ? '70%' : '85%' }}>
        {message.sender === 'user' ? (
          <Paper
            className="message-bubble user"
            sx={{
              padding: '12px 16px',
              backgroundColor: '#00A3E0',
              color: '#fff',
              borderRadius: '16px 16px 4px 16px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography variant="body1" sx={{ fontSize: '15px', lineHeight: 1.6 }}>
              {message.text}
            </Typography>
          </Paper>
        ) : (
          <Paper
            className="message-bubble bot"
            sx={{
              padding: '16px',
              backgroundColor: message.isError ? '#ffebee' : '#F8F8F8',
              color: '#303030',
              borderRadius: '16px 16px 16px 4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Description */}
            {message.description && (
              <Box className="description-text">
                {renderFormattedDescription(message.description, message.results || [])}
              </Box>
            )}

            {/* Simple text message (error or text-only response) */}
            {message.text && (
              <Typography variant="body1" sx={{ fontSize: '15px', lineHeight: 1.6 }}>
                {message.text}
              </Typography>
            )}

            {/* Results (tables/charts) */}
            {message.results && message.results.map((result, idx) => (
              <Box key={idx}>
                {renderResult(result, idx, message.results, message.id)}
              </Box>
            ))}
          </Paper>
        )}
      </Box>
      {message.sender === 'user' && (
        <Avatar
          sx={{
            bgcolor: '#005587',
            width: 36,
            height: 36,
            ml: 2,
            mt: 0.5,
          }}
        >
          <PersonIcon sx={{ fontSize: 20 }} />
        </Avatar>
      )}
    </Box>
  );
});

MessageItem.displayName = 'MessageItem';

// Memoized Input Area Component - isolates input state to prevent message list re-renders
const InputArea = memo(({ 
  onSendMessage, 
  isLoading, 
  isConversationLimitReached, 
  userMessageCount,
  queryType,
  setQueryType,
  hideDataExplorationAgent = false,
}) => {
  const [input, setInput] = useState('');
  const isInputLimitReached = input.length >= MAX_INPUT_CHARACTERS;

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isLoading && !isConversationLimitReached && !isInputLimitReached) {
        onSendMessage(input, queryType);
        setInput('');
      }
    }
  }, [input, queryType, isLoading, isConversationLimitReached, isInputLimitReached, onSendMessage]);

  const handleSend = useCallback(() => {
    if (input.trim() && !isLoading && !isConversationLimitReached && !isInputLimitReached) {
      onSendMessage(input, queryType);
      setInput('');
    }
  }, [input, queryType, isLoading, isConversationLimitReached, isInputLimitReached, onSendMessage]);

  const handleInputChange = useCallback((e) => {
    const newValue = e.target.value.slice(0, MAX_INPUT_CHARACTERS);
    setInput(newValue);
  }, []);

  // Expose setInput for external use (followup questions)
  useEffect(() => {
    window.__chatbotSetInput = setInput;
    return () => {
      delete window.__chatbotSetInput;
    };
  }, []);

  return (
    <Box className="input-container">
      <Paper
        className="input-wrapper"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '12px 16px',
          borderRadius: '24px',
          border: '1px solid #e0e0e0',
          backgroundColor: '#fff',
          maxWidth: '900px',
          margin: '0 auto',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            placeholder={isConversationLimitReached 
              ? `Conversation limit reached (${MAX_CONVERSATION_MESSAGES} messages). Start a new chat.`
              : "Type your message here..."}
            value={input}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            variant="standard"
            disabled={isConversationLimitReached}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '15px',
                fontFamily: 'Montserrat',
                overflow: 'auto',
                wordBreak: 'break-word',
              },
            }}
            sx={{
              '& .MuiInputBase-input': {
                padding: '4px 0',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
              },
              '& .MuiInputBase-root': {
                overflow: 'hidden',
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!input.trim() || isLoading || isConversationLimitReached || isInputLimitReached}
            sx={{
              background: 'linear-gradient(135deg, #2d89fb 0%, #09b2e0 100%)',
              color: '#fff',
              width: 40,
              height: 40,
              borderRadius: '15px',
              flexShrink: 0,
              mt: 0.5,
              '&:hover': {
                background: (input.trim() && !isConversationLimitReached && !isInputLimitReached) ? 'linear-gradient(135deg,rgb(26, 128, 252) 0%,rgb(9, 164, 207) 100%)' : '#e0e0e0',
              },
              '&.Mui-disabled': {
                background: 'rgba(128, 181, 247, 0.93)',
                color: 'rgba(255, 255, 255, 0.85)',
                '& .MuiSvgIcon-root': {
                  color: 'rgba(255, 255, 255, 0.85)',
                },
              },
            }}
          >
            <SendIcon sx={{ fontSize: 20, transform: 'rotate(-45deg) translateY(-2px) translateX(3px)', transformOrigin: 'center', }} />
          </IconButton>
        </Box>
        {!hideDataExplorationAgent && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, pt: 1 }}>
          <Select
            value={queryType}
            onChange={(e) => {
              const newValue = e.target.value;
              // Allow clearing selection by clicking the same value again
              if (newValue === queryType) {
                setQueryType('');
              } else {
                setQueryType(newValue);
              }
            }}
            displayEmpty
            disabled={isConversationLimitReached}
            variant="standard"
            disableUnderline
            renderValue={(selected) => {
              if (!selected) {
                return <span style={{ color: '#5f6368', fontSize: '13px' }}>Select a query type</span>;
              }
              // Map internal values to display labels
              const displayLabels = {
                'DATA_QUERY': 'SiMS Data Exploration Agent',
                'BUSINESS_QUERY': 'SiMS Knowledge Assistant'
              };
              return <span style={{ color: '#202124', fontSize: '13px', fontWeight: 500 }}>{displayLabels[selected] || selected}</span>;
            }}
            sx={{
              fontSize: '13px',
              fontFamily: 'Montserrat',
              height: '32px',
              backgroundColor: queryType ? '#e3f2fd' : '#f1f3f4',
              borderRadius: '16px',
              padding: '0 12px',
              minWidth: '140px',
              '& .MuiSelect-select': {
                padding: '6px 24px 6px 0 !important',
                minHeight: 'auto',
                backgroundColor: 'transparent !important',
              },
              '&:hover': {
                backgroundColor: queryType ? '#bbdefb' : '#e8eaed',
              },
              '&.Mui-focused': {
                backgroundColor: queryType ? '#e3f2fd' : '#f1f3f4',
              },
              '& .MuiSelect-icon': {
                right: '8px',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: '8px',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                  mt: 0.5,
                  minWidth: '160px',
                },
              },
            }}
          >
              <MenuItem value="" sx={{ fontSize: '13px', py: 1 }}>
                <em style={{ color: '#999', fontStyle: 'normal' }}>None</em>
              </MenuItem>
              <MenuItem 
                value="DATA_QUERY"
                sx={{
                  fontSize: '13px',
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#f1f3f4',
                    '&:hover': {
                      backgroundColor: '#e8eaed',
                    },
                  },
                }}
              >
                SiMS Data Exploration Agent
              </MenuItem>
              <MenuItem 
                value="BUSINESS_QUERY"
                sx={{
                  fontSize: '13px',
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#f1f3f4',
                    '&:hover': {
                      backgroundColor: '#e8eaed',
                    },
                  },
                }}
              >
                SiMS Knowledge Assistant
              </MenuItem>
          </Select>
        </Box>
        )}
      </Paper>
      <Box
        sx={{
          display: 'flex',
          justifyContent: isInputLimitReached ? 'space-between' : 'center',
          alignItems: 'center',
          maxWidth: '900px',
          margin: '8px auto 0',
          px: 1,
        }}
      >
        {isConversationLimitReached ? (
          <Typography
            variant="caption"
            sx={{
              textAlign: 'center',
              color: '#d63031',
              fontSize: '12px',
              marginTop: '12px',
              fontWeight: 400,
            }}
          >
            {`Conversation limit reached (${userMessageCount}/${MAX_CONVERSATION_MESSAGES} messages). Please start a new chat.`}
          </Typography>
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{
                textAlign: isInputLimitReached ? 'left' : 'center',
                color: isInputLimitReached ? '#d63031' : '#808080',
                fontSize: '12px',
                marginTop: '12px',
                fontWeight: 400,
                flex: isInputLimitReached ? 1 : 'unset',
              }}
            >
              {'Always review the accuracy of the responses. For support, please connect with the SiMS Admin Team.'}
            </Typography>

            {isInputLimitReached && (
              <Typography
                variant="caption"
                sx={{
                  color:
                    input.length > MAX_INPUT_CHARACTERS * 0.9
                      ? (isInputLimitReached ? '#DA291C' : '#FE8A12') // IQVIA Red / Orange
                      : '#808080',
                  fontSize: '11px',
                  marginTop: '12px',
                  fontWeight: 500,
                  ml: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                {input.length}/{MAX_INPUT_CHARACTERS}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
});

InputArea.displayName = 'InputArea';

// Memoized Chat History Item
const ChatHistoryItem = memo(({ chat, isSelected, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect(chat);
  }, [chat, onSelect]);

  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        selected={isSelected}
        onClick={handleClick}
        sx={{
          borderRadius: '8px',
          paddingRight: '48px',
          '&.Mui-selected': {
            backgroundColor: '#EBF5FF',
            borderLeft: '3px solid #00A3E0',
          },
          '&.Mui-selected:hover': {
            backgroundColor: '#d4ecff',
          },
          '&:hover': {
            backgroundColor: '#f2f3f4',
          },
        }}
      >
        <ListItemText
          primary={chat.title}
          primaryTypographyProps={{
            fontSize: '14px',
            fontWeight: isSelected ? 600 : 500,
            noWrap: true,
            color: isSelected ? '#005587' : '#303030',
          }}
        />
      </ListItemButton>
    </ListItem>
  );
});

ChatHistoryItem.displayName = 'ChatHistoryItem';

const ChatBot = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleOpenSupplyRiskHeatMap = useCallback(() => {
    dispatch(setAnalyzeState("selectedAnalysisType", "Supply Risk Heat Map"));
    dispatch(setAnalyzeState("selectedAnalysisTypeID", 15));
    // Prevent Analyze Header from resetting the selection on mount.
    dispatch(setLoginState("isGCountryChangedAnalyze", true));
    navigate("/analyze");
  }, [dispatch, navigate]);

  const { 
    messages, 
    isLoading, 
    chatHistory, 
    currentChatId, 
    currentConversationId,
    isGenieConversation,
    currentDatabaseChatId,
    errorMessage,
    isError,
    followupQuestions,
    isLoadingFollowupQuestions,
    currentQuestion,
    dashboardCards,
    isLoadingDashboardCards,
    mostAskedQuestions,
    isLoadingMostAskedQuestions,
  } = useSelector((state) => state.chatbot, shallowEqual);
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [queryType, setQueryType] = useState('');

  useEffect(() => {
    if (HIDE_DATA_EXPLORATION_AGENT && queryType === 'DATA_QUERY') {
      setQueryType('');
    }
  }, [queryType]);

  const [kpiSummaryCards, setKpiSummaryCards] = useState(KPI_SUMMARY_CARDS_DEFAULT);
  const [alertCards, setAlertCards] = useState(ALERT_CARDS_DEFAULT);

  // Fetch KPI config from /kpi-config.json — update that file on the server to change cards without a rebuild
  useEffect(() => {
    fetch('/kpi-config.json')
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data) && data.length > 0) setKpiSummaryCards(data); })
      .catch(() => { /* silently keep fallback */ });
  }, []);

  // Fetch alerts config from /alerts-config.json so payload can be updated without a rebuild
  useEffect(() => {
    fetch('/alerts-config.json')
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setAlertCards(data);
        }
      })
      .catch(() => { /* silently keep fallback */ });
  }, []);

  // Country / Brand / SKU forecast data from /forecast-by-sku.json
  const [forecastBySku, setForecastBySku] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('United States');
  const [selectedBrand, setSelectedBrand] = useState('SONOVUE');
  const [selectedSku, setSelectedSku] = useState('');
  // Constrained Factor (%): applied to Unconstrained Demand to derive Constrained Forecast
  const [constrainedFactor, setConstrainedFactor] = useState(String(DEFAULT_CONSTRAINED_FACTOR));

  useEffect(() => {
    fetch('/forecast-by-sku.json')
      .then((r) => r.json())
      .then((data) => {
        if (!data?.countries) return;
        setForecastBySku(data);
        const country = data.defaults?.country && data.countries[data.defaults.country]
          ? data.defaults.country
          : Object.keys(data.countries)[0];
        const brands = getBrandOptions(data, country);
        const brand = data.defaults?.brand && brands.includes(data.defaults.brand)
          ? data.defaults.brand
          : (brands[0] || '');
        const skus = getSkuOptions(data, country, brand);
        const sku = data.defaults?.sku && skus.includes(data.defaults.sku)
          ? data.defaults.sku
          : (skus[0] || '');
        setSelectedCountry(country || '');
        setSelectedBrand(brand);
        setSelectedSku(sku);
      })
      .catch(() => { /* leave null until available */ });
  }, []);

  const countryOptions = useMemo(() => getCountryOptions(forecastBySku), [forecastBySku]);
  const brandOptions = useMemo(
    () => getBrandOptions(forecastBySku, selectedCountry),
    [forecastBySku, selectedCountry]
  );
  const skuOptions = useMemo(
    () => getSkuOptions(forecastBySku, selectedCountry, selectedBrand),
    [forecastBySku, selectedCountry, selectedBrand]
  );

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    const brands = getBrandOptions(forecastBySku, country);
    const nextBrand = brands.includes(selectedBrand) ? selectedBrand : (brands[0] || '');
    setSelectedBrand(nextBrand);
    const skus = getSkuOptions(forecastBySku, country, nextBrand);
    setSelectedSku(skus[0] || '');
  };

  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    const skus = getSkuOptions(forecastBySku, selectedCountry, brand);
    setSelectedSku(skus.includes(selectedSku) ? selectedSku : (skus[0] || ''));
  };

  // Selection-scoped dashboard charts + KPI totals
  const productScopedDashboard = useMemo(() => {
    const points = getSkuPoints(forecastBySku, selectedCountry, selectedBrand, selectedSku);
    const forecastData = buildForecastChartFromSku(points, constrainedFactor);
    const kpiTotals = computeForecastKpiTotals(points, constrainedFactor);

    return {
      signalsData: varySignalsByCategory(
        dashboardCards?.adherenceToProductObjectives?.data,
        selectedBrand
      ),
      buildUpData: varyForecastBuildUp(
        dashboardCards?.quotasPendingApproval?.data,
        selectedBrand
      ),
      forecastData,
      kpiTotals,
      buildUpTitle: `Forecast build-up by driver (${selectedBrand}™)`,
    };
  }, [dashboardCards, forecastBySku, selectedCountry, selectedBrand, selectedSku, constrainedFactor]);

  const displayKpiCards = useMemo(() => {
    const { unconstrained, constrained, forecastMonths } = productScopedDashboard.kpiTotals || {};
    const hasSkuForecast = (forecastMonths || 0) > 0;
    const signalsTotal = (productScopedDashboard.signalsData || []).reduce((sum, row) => {
      const raw = row?.demandVsSupply ?? row?.value;
      const num = typeof raw === 'number' ? raw : parseFloat(raw);
      return sum + (Number.isFinite(num) ? num : 0);
    }, 0);

    return (kpiSummaryCards || []).map((card) => {
      if (card.key === 'unconstrainedDemand' && hasSkuForecast) {
        return {
          ...card,
          value: formatKpiUnits(unconstrained),
          subtitle: `${forecastMonths}-month forecast`,
        };
      }
      if (card.key === 'constrainedForecast' && hasSkuForecast) {
        return {
          ...card,
          value: formatKpiUnits(constrained),
          subtitle: 'after supply limits',
        };
      }
      if (card.key === 'signalsDetected' && (productScopedDashboard.signalsData || []).length > 0) {
        return {
          ...card,
          value: String(Math.round(signalsTotal)),
        };
      }
      return card;
    });
  }, [kpiSummaryCards, productScopedDashboard.kpiTotals, productScopedDashboard.signalsData]);

  const [tablePagination, setTablePagination] = useState({});
  const [markdownTablePagination, setMarkdownTablePagination] = useState({});
  const messagesEndRef = useRef(null);
  
  // Chart edit state
  const [chartEditDialog, setChartEditDialog] = useState({
    open: false,
    chartId: null,
    chartType: null, // 'table' or 'markdown'
    originalData: null,
    xAxis: '',
    yAxis: [], // Changed to array for multiselect
    legend: '',
    value: '', // For pie/radialbar charts
    selectedChartType: 'bar', // Chart visualization type: 'line', 'area', 'bar', 'scatter', 'pie', 'radialbar'
    availableColumns: [],
  });
  
  // Store chart configurations persistently
  const [chartConfigs, setChartConfigs] = useState({});
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    alert: null,
  });
  
  // Loading messages for chatbot-like animation
  const loadingMessages = [
    "Understanding the question...",
    "Fetching the right data...",
    "Drafting a step-by-step plan for the answer...",
    "Putting the last bits together..."
  ];
  const [currentLoadingMessageIndex, setCurrentLoadingMessageIndex] = useState(0);
  const [messageOpacity, setMessageOpacity] = useState(1);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle loading message animation
useEffect(() => {
  if (!isLoading) {
    setCurrentLoadingMessageIndex(0);
    setMessageOpacity(1);
    return;
  }

  // Reset to first message when loading starts
  setCurrentLoadingMessageIndex(0);
  setMessageOpacity(1);

  const messageDuration = 4000; // 2 seconds per message
  const fadeDuration = 400;     // 400ms for fade transition
  let currentIndex = 0;

  const cycleMessages = () => {
    // Fade out current message
    setMessageOpacity(0);

    setTimeout(() => {
      // Only advance if not at the last message
      if (currentIndex < loadingMessages.length - 1) {
        currentIndex++;
        setCurrentLoadingMessageIndex(currentIndex);

        // Fade in new message
        setTimeout(() => {
          setMessageOpacity(1);
        }, 50);
      } else {
        // Stay on the last message — stop cycling
        clearInterval(interval);
        setMessageOpacity(1);
      }
    }, fadeDuration);
  };

  const interval = setInterval(cycleMessages, messageDuration);

  return () => clearInterval(interval);
}, [isLoading, loadingMessages.length]);


  // Track if we've already checked for dashboard cards
  const hasCheckedDashboardCards = React.useRef(false);

  // Dashboard cards are preloaded in MainLayout.js when user logs in
  // This useEffect only dispatches if data hasn't been loaded yet (e.g., direct navigation)
  useEffect(() => {
    // Only check once per component mount
    if (hasCheckedDashboardCards.current) return;
    hasCheckedDashboardCards.current = true;

    // Check if any of the 8 KPI cards have data
    const hasData = dashboardCards && (
      (dashboardCards.adherenceToProductObjectives?.data?.length > 0) ||
      (dashboardCards.quotasAtRisk?.data?.length > 0) ||
      (dashboardCards.quotasPendingSubmission?.data?.length > 0) ||
      (dashboardCards.quotasPendingApproval?.data?.length > 0) ||
      (dashboardCards.topCountriesHighestDemand?.data?.length > 0) ||
      (dashboardCards.topCountriesMostExcess?.data?.length > 0) ||
      (dashboardCards.topQuotaBreachesVsInitial?.data?.length > 0) ||
      (dashboardCards.topProductExcessGrowth?.data?.length > 0)
    );
    
    // Only dispatch if not already loading and no data exists
    if (!isLoadingDashboardCards && !hasData) {
      console.log("🔄 [ChatBot] Dashboard cards not preloaded, fetching now...");
      dispatch(getDashboardCards());
    } else if (hasData) {
      console.log("✅ [ChatBot] Dashboard cards already preloaded");
    } else if (isLoadingDashboardCards) {
      console.log("⏳ [ChatBot] Dashboard cards still loading from preload...");
    }
  }, [dashboardCards, dispatch, isLoadingDashboardCards]);

  // Track if we've tried to fetch frequently asked questions
  const [hasTriedFrequentlyAsked, setHasTriedFrequentlyAsked] = useState(false);

  // Fetch frequently asked questions if not already loaded
  useEffect(() => {
    const hasData = mostAskedQuestions && mostAskedQuestions.length > 0;
    
    if (!isLoadingMostAskedQuestions && !hasData && !hasTriedFrequentlyAsked) {
      // First try to get frequently asked questions from user data
      setHasTriedFrequentlyAsked(true);
      dispatch(getFrequentlyAskedQuestions());
    }
  }, [dispatch, mostAskedQuestions, isLoadingMostAskedQuestions, hasTriedFrequentlyAsked]);

  // Fallback to most asked questions if frequently asked questions are empty after trying
  useEffect(() => {
    if (hasTriedFrequentlyAsked && 
        !isLoadingMostAskedQuestions && 
        mostAskedQuestions && 
        Array.isArray(mostAskedQuestions) && 
        mostAskedQuestions.length === 0) {
      // If we tried frequently asked questions but got nothing, try the fallback
      dispatch(getMostAskedQuestions());
    }
  }, [dispatch, mostAskedQuestions, isLoadingMostAskedQuestions, hasTriedFrequentlyAsked]);

  useEffect(() => {
    // Fetch user chat bot history when chatbot loads only if not already loaded
    const hasData = chatHistory && chatHistory.length > 1;
    
    if (!hasData) {
      dispatch(getUserChatBotHistory());
    }
  }, [dispatch, chatHistory]);

  // Fetch followup questions after bot message is added or when conversation history is loaded
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.sender === 'bot' && !lastMessage.isError) {
      // Get the last user question - either from currentQuestion (new message) or from messages (loaded history)
      let userQuestion = currentQuestion;
      
      // If currentQuestion is not set, try to find the last user message from the loaded messages
      if (!userQuestion && messages.length > 1) {
        // Find the last user message before the bot message
        for (let i = messages.length - 2; i >= 0; i--) {
          if (messages[i].sender === 'user') {
            userQuestion = messages[i].text;
            break;
          }
        }
      }
      
      // Only fetch followup questions if we have both user question and AI response
      if (userQuestion) {
        const aiResponse = lastMessage.description || lastMessage.text || '';
        if (aiResponse) {
      // Clear previous followup questions
      dispatch(clearFollowupQuestions());
      
      // Fetch new followup questions
        dispatch(getFollowupQuestions({
            userQuestion: userQuestion,
          aiResponse: aiResponse,
          conversationId: currentConversationId || ''
        }));
        }
      }
    } else if (messages.length === 0) {
      // Clear followup questions when messages are cleared
      //dispatch(clearFollowupQuestions());
    }
  }, [messages, currentQuestion, currentConversationId, dispatch]);

  // Check for pending question from floating chatbot
  useEffect(() => {
    const pendingQuestion = sessionStorage.getItem('pendingQuestion');
    handleNewChat();
    if (pendingQuestion) {
      // Clear the pending question
      sessionStorage.removeItem('pendingQuestion');
      // Set it in the input
      //setInput(pendingQuestion);
      // Use setTimeout to ensure state is updated before sending
      setTimeout(() => {
        // Trigger send by simulating the send action
        const questionText = pendingQuestion;
        if (!questionText || !questionText.trim()) return;

        // Add user message to Redux store
        dispatch(addUserMessage({ text: questionText }));
        //setInput('');
        
        // Send the message
        (async () => {
          try {
            // If this is the first message or no conversation ID exists, start a new conversation
            //if (!currentConversationId) {
              const resultAction = await dispatch(startConversation({ question: questionText, queryType: queryType }));
              
              if (startConversation.rejected.match(resultAction)) {
                // Handle error
                dispatch(addErrorMessage({ 
                  text: `Sorry, I encountered an error: ${resultAction.payload || 'Unknown error'}` 
                }));
              }
            //} 
            // else {
            //   // Continue existing conversation
            //   const resultAction = await dispatch(continueChat({ 
            //     conversationId: currentConversationId, 
            //     question: questionText 
            //   }));
              
            //   if (continueChat.rejected.match(resultAction)) {
            //     // Handle error
            //     dispatch(addErrorMessage({ 
            //       text: `Sorry, I encountered an error: ${resultAction.payload || 'Unknown error'}` 
            //     }));
            //   }
            // }
          } catch (err) {
            console.error('Error sending message:', err);
            dispatch(addErrorMessage({ 
              text: `Sorry, I encountered an error: ${err.message || 'Unknown error'}` 
            }));
          }
        })();
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = useCallback((uniqueKey, newPage) => {
    setTablePagination(prev => ({
      ...prev,
      [uniqueKey]: {
        ...prev[uniqueKey],
        page: newPage,
      }
    }));
  }, []);

  const handleChangeRowsPerPage = useCallback((uniqueKey, event) => {
    setTablePagination(prev => ({
      ...prev,
      [uniqueKey]: {
        page: 0,
        rowsPerPage: parseInt(event.target.value, 10),
      }
    }));
  }, []);

  const handleMarkdownTableChangePage = useCallback((tableKey, newPage) => {
    setMarkdownTablePagination(prev => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        page: newPage,
      }
    }));
  }, []);

  const handleMarkdownTableChangeRowsPerPage = useCallback((tableKey, event) => {
    setMarkdownTablePagination(prev => ({
      ...prev,
      [tableKey]: {
        page: 0,
        rowsPerPage: parseInt(event.target.value, 10),
      }
    }));
  }, []);

  // Helper function to convert scientific notation to regular number
  const convertScientificNotation = (value, isNumericColumn = false) => {
    if (value === null || value === undefined || value === '') {
      return value;
    }
    
    // If it's already a number, return it
    if (typeof value === 'number') {
      return value;
    }
    
    // Only process if it's a numeric column or if it looks like scientific notation
    if (typeof value === 'string') {
      // Check for scientific notation pattern (e.g., "2.105567304E9", "1.46040222E8", "3.30026288E8")
      const scientificNotationPattern = /^[\d.]+[Ee][+-]?\d+$/;
      const trimmedValue = value.trim();
      
      if (scientificNotationPattern.test(trimmedValue)) {
        // Convert to number
        const numValue = parseFloat(trimmedValue);
        return isNaN(numValue) ? value : numValue;
      }
      
      // If it's a numeric column, try to parse it as a number
      if (isNumericColumn) {
        const numValue = parseFloat(trimmedValue);
        if (!isNaN(numValue)) {
          return numValue;
        }
      }
    }
    
    return value;
  };

  // Helper function to format column names: replace underscores with spaces, then Title Case
  const formatColumnName = (name) => {
    if (!name) return '';
    // Replace underscores with spaces
    let formatted = name.replace(/_/g, ' ');
    // Title Case: First letter and letter after space should be caps, rest lowercase
    formatted = formatted.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    return formatted;
  };

  // Helper function to get the last AI bot description
  const getLastBotDescription = () => {
    // Find the last message with sender === 'bot' and has description
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].sender === 'bot' && messages[i].description) {
        // Remove markdown code blocks and chart configs from description
        let cleanDescription = messages[i].description;
        // Remove Highcharts code blocks
        cleanDescription = cleanDescription.replace(/```highcharts\s*\n[\s\S]*?\n```/g, '');
        // Remove Highchart option patterns
        const highchartPatterns = [
          'Highchart option for visualization:',
          'Highchart option:',
          'Highcharts option:',
          'Highcharts config:',
          'Highcharts option',
          'Highchart option',
          'Chart option',
          'Charts option'
        ];
        for (const pattern of highchartPatterns) {
          const index = cleanDescription.toLowerCase().indexOf(pattern.toLowerCase());
          if (index !== -1) {
            cleanDescription = cleanDescription.substring(0, index).trim();
            break;
          }
        }
        return cleanDescription.trim();
      }
    }
    return null;
  };

  // Helper function to add header to PDF pages
  const addPDFHeader = (pdf) => {
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(62, 179, 230); 
      pdf.text('SiMS Data Exploration Agent', pdf.internal.pageSize.getWidth() / 2, 15, {
        align: 'center'
      });
    }
  };

  // Helper function to add footer to PDF pages
  const addPDFFooter = (pdf) => {
    const pageCount = pdf.internal.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.height;
    
    // Load logo image - try both jpg and png
    let logoDataUrl = null;
    try {
      // Try jpg first
      try {
        const logoPath = require('../../assets/images/iqvia-logo-mini.png');
        // In webpack/bundler environments, require() returns the processed path
        logoDataUrl = logoPath;
      } catch {
        // Fallback to png
        try {
          const logoPath = require('../../assets/images/iqvia-logo.jpg');
          logoDataUrl = logoPath;
        } catch {
          // If both fail, logoDataUrl remains null
        }
      }
    } catch (logoError) {
      console.warn('Could not load logo:', logoError);
    }
    
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      
      // Draw border line above footer
      pdf.setDrawColor(62, 179, 230); // Light gray color
      pdf.setLineWidth(0.5);
      pdf.line(14, pageHeight - 21, pageWidth - 14, pageHeight - 21);
      
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(62, 179, 230);
      
      // Left side text
      pdf.text(
        'Always review the accuracy of the responses. For support, please connect with the SiMS Admin Team.',
        14,
        pageHeight - 15
      );
      pdf.setTextColor(0,0,0);
      pdf.text(
        `Copyright © ${new Date().getFullYear()} IQVIA All rights reserved.`,
        14,
        pageHeight - 10
      );
      
      // Right side logo
      if (logoDataUrl) {
        try {
          const logoWidth = 40;
          const logoHeight = 9;
          const logoX = pageWidth - logoWidth - 14;
          const logoY = pageHeight - 20;
          // Determine image type - default to JPEG, fallback to PNG
          const imgType = typeof logoDataUrl === 'string' && (logoDataUrl.includes('.jpg') || logoDataUrl.includes('.jpeg')) ? 'JPEG' : 'PNG';
          pdf.addImage(logoDataUrl, imgType, logoX, logoY, logoWidth, logoHeight);
        } catch (imgError) {
          console.warn('Could not add logo to PDF:', imgError);
        }
      }
    }
  };

  const handleOpenAlertDialog = useCallback((alertCard) => {
    setAlertDialog({ open: true, alert: alertCard });
  }, []);

  const handleCloseAlertDialog = useCallback(() => {
    setAlertDialog({ open: false, alert: null });
  }, []);

  // Common helper function to generate Excel file from headers and data
  const generateExcelFile = async (headers, dataRows, fileName, sheetName = 'Data') => {
    const processedDataRows = dataRows.map(row =>
      row.map(cell => {
        const convertedCell = convertScientificNotation(cell, false);
        if (typeof convertedCell === 'number') {
          return convertedCell.toLocaleString();
        }
        return convertedCell;
      })
    );

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);
    worksheet.addRows([headers, ...processedDataRows]);
    worksheet.columns = headers.map(() => ({ width: 15 }));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${fileName}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // Common helper function to generate PDF file from headers and data
  const generatePDFFile = (headers, dataRows, fileName, startY = 30) => {
    const pdf = new jsPDF();
    
    // Prepare headers with formatted column names
    const formattedHeaders = headers.map(header => formatColumnName(header));
    
    // Format data rows (convert scientific notation and handle numeric values)
    const formattedDataRows = dataRows.map((row, rowIdx) => 
      row.map((cell, cellIdx) => {
        // Convert scientific notation if it's a string
        const convertedCell = convertScientificNotation(cell, false);
        if (typeof convertedCell === 'number') {
          return convertedCell.toLocaleString();
        }
        return String(convertedCell || '');
      })
    );
    
    autoTable(pdf, {
      head: [formattedHeaders],
      body: formattedDataRows,
      startY: startY,
      theme: 'grid',
      headStyles: {
        fillColor: [0, 85, 135], // #005587
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3,
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250], // #f8f9fa
      },
      margin: { top: 25, bottom: 30 },
      didDrawPage: function (data) {
        // Add header on each page
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(62, 179, 230); 
        pdf.text('SiMS Data Exploration Agent', pdf.internal.pageSize.getWidth() / 2, 15, {
          align: 'center'
        });
      },
    });

    // Add header and footer to all pages
    addPDFHeader(pdf);
    addPDFFooter(pdf);

    pdf.save(`${fileName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Download table as Excel
  const downloadTableAsExcel = useCallback(async (result, fileName = 'table_data') => {
    if (!result.columns || !result.data) return;
    const headers = result.columns.map(col => col.name);
    try {
      await generateExcelFile(headers, result.data, fileName, 'Data');
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Failed to download table as Excel. Please try again.');
    }
  }, [generateExcelFile]);

  // Download chart data as PDF (same as Excel - table format)
  const downloadChartAsPDF = useCallback(async (result, fileName = 'chart_data', chartElementId = null, aiDescription = null) => {
    if (!result.columns || !result.data) return;

    try {
      const pdf = new jsPDF();
      let startY = 23;
      
      // Add AI description at the top if provided
      if (aiDescription) {
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        
        // Split long text into multiple lines
        const splitText = pdf.splitTextToSize(aiDescription, pdf.internal.pageSize.getWidth() - 28);
        pdf.text(splitText, 14, startY);
        startY += splitText.length * 5 + 10; // Add spacing after description
      }
      
      // Capture chart image if chartElementId is provided
      if (chartElementId) {
        try {
          const chartElement = document.getElementById(chartElementId);
          if (chartElement) {
            const canvas = await html2canvas(chartElement, {
              backgroundColor: '#ffffff',
              scale: 2,
              logging: false,
            });
            
            const imgData = canvas.toDataURL('image/png');
            const imgWidth = pdf.internal.pageSize.getWidth() - 28; // Leave margins
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            
            // Add chart image
            pdf.addImage(imgData, 'PNG', 14, startY, imgWidth, imgHeight);
            startY += imgHeight + 10;
          }
        } catch (imgError) {
          console.warn('Could not capture chart image:', imgError);
          // Continue without image if capture fails
        }
      }
      
      // Prepare headers and data with formatted column names
      const headers = result.columns.map(col => formatColumnName(col.name));
      const tableData = result.data.map(row => 
        row.map(cell => {
          // Format numeric values
          if (typeof cell === 'number') {
            return cell.toLocaleString();
          }
          return String(cell || '');
        })
      );

      // Add table using autoTable
      autoTable(pdf, {
        head: [headers],
        body: tableData,
        startY: startY,
        theme: 'grid',
        headStyles: {
          fillColor: [0, 85, 135], // #005587
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10,
        },
        bodyStyles: {
          fontSize: 9,
          cellPadding: 3,
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250], // #f8f9fa
        },
        margin: { top: 25, bottom: 30 },
        didDrawPage: function (data) {
          // Add header on each page
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(62, 179, 230); 
          pdf.text('SiMS Data Exploration Agent', pdf.internal.pageSize.getWidth() / 2, 15, {
            align: 'center'
          });
        },
      });

      // Add header and footer to all pages
      addPDFHeader(pdf);
      addPDFFooter(pdf);

      pdf.save(`${fileName}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to download chart as PDF. Please try again.');
    }
  }, [addPDFHeader, addPDFFooter, formatColumnName]);

  // Download table as PDF using jsPDF autoTable
  const downloadTableAsPDF = useCallback((result, fileName = 'table_data') => {
    if (!result.columns || !result.data) return;

    try {
      const headers = result.columns.map(col => col.name);
      generatePDFFile(headers, result.data, fileName, 30);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to download table as PDF. Please try again.');
    }
  }, [generatePDFFile]);

  // Download chart data as Excel
  const downloadChartAsExcel = useCallback(async (result, fileName = 'chart_data') => {
    if (!result.columns || !result.data) return;
    const headers = result.columns.map(col => col.name);
    try {
      await generateExcelFile(headers, result.data, fileName, 'Chart Data');
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Failed to download chart as Excel. Please try again.');
    }
  }, [generateExcelFile]);

  const renderTable = useCallback((result, uniqueKey = '0') => {
    if (!result.columns || result.columns.length === 0 || !result.data || result.data.length === 0) {
      return <Typography>No data available</Typography>;
    }

    // Process data to convert scientific notation for numeric columns
    const processedData = result.data.map(row => {
      return row.map((cell, idx) => {
        const col = result.columns[idx];
        const isNumeric = col.type && (
          col.type.toUpperCase().includes('INT') ||
          col.type.toUpperCase().includes('DOUBLE') ||
          col.type.toUpperCase().includes('FLOAT') ||
          col.type.toUpperCase().includes('DECIMAL') ||
          col.type.toUpperCase().includes('NUMERIC')
        );
        return convertScientificNotation(cell, isNumeric);
      });
    });

    const page = tablePagination[uniqueKey]?.page || 0;
    const rowsPerPage = tablePagination[uniqueKey]?.rowsPerPage || 20;

    return (
      <TableRenderer
        columns={result.columns}
        data={processedData}
        uniqueKey={uniqueKey}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, newPage) => handleChangePage(uniqueKey, newPage)}
        onRowsPerPageChange={(event) => handleChangeRowsPerPage(uniqueKey, event)}
        onDownloadExcel={downloadTableAsExcel}
        onDownloadPDF={downloadTableAsPDF}
        downloadData={result}
        maxHeight={600}
        stickyHeader={true}
        tableContainerSx={{
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(200, 201, 202, 0.89)',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(173, 170, 170, 0.98)',
          },
        }}
      />
    );
  }, [tablePagination, handleChangePage, handleChangeRowsPerPage, downloadTableAsExcel, downloadTableAsPDF, convertScientificNotation]);

  // Helper function to render formatted description with markdown
  const renderFormattedDescription = (description, allResults = []) => {
    if (!description) return null;

    // Check for Highcharts code block (````highcharts...```)
    const highchartsMatch = description.match(/```highcharts\s*\n([\s\S]*?)\n```/);
    if (highchartsMatch) {
      try {
        const chartConfig = JSON.parse(highchartsMatch[1]);
        const textBeforeChart = description.substring(0, highchartsMatch.index);
        const textAfterChart = description.substring(highchartsMatch.index + highchartsMatch[0].length);
        
        return (
          <>
            {textBeforeChart && renderTextContent(textBeforeChart, allResults)}
            {renderHighchartsAsRechart(chartConfig)}
            {textAfterChart && renderTextContent(textAfterChart, allResults)}
            {shouldShowOutputVerificationDisclaimer(description) && (
              <OutputVerificationDisclaimer />
            )}
          </>
        );
      } catch (e) {
        console.error('Failed to parse Highcharts config:', e);
      }
    }

    // Check for Highchart JSON inline - handle multiple variations
    // Patterns: "Highchart option:", "Highchart option for visualization:", "Highcharts option:", etc.
    const highchartPatterns = [
      'Highchart option for visualization:',
      'Highchart option:',
      'Highcharts option:',
      'Highcharts config:',
      'Highcharts option',
      'Highchart option',
      'Chart option',
      'Charts option'
    ];
    
    let highchartIndex = -1;
    for (const pattern of highchartPatterns) {
      highchartIndex = description.toLowerCase().indexOf(pattern.toLowerCase());
      if (highchartIndex !== -1) break;
    }
    
    if (highchartIndex !== -1) {
      try {
        // Find the start of JSON (first { after the phrase)
        const jsonStart = description.indexOf('{', highchartIndex);
        if (jsonStart !== -1) {
          // Find matching closing brace by counting braces
          let braceCount = 0;
          let jsonEnd = -1;
          for (let i = jsonStart; i < description.length; i++) {
            if (description[i] === '{') braceCount++;
            if (description[i] === '}') {
              braceCount--;
              if (braceCount === 0) {
                jsonEnd = i + 1;
                break;
              }
            }
          }
          
          if (jsonEnd !== -1) {
            const jsonStr = description.substring(jsonStart, jsonEnd);
            const chartConfig = JSON.parse(jsonStr);
            const textBeforeChart = description.substring(0, highchartIndex);
            const textAfterChart = description.substring(jsonEnd).trim();
            
            return (
              <>
                {textBeforeChart && renderTextContent(textBeforeChart, allResults)}
                <Box sx={{ my: 2 }}>
                  {renderHighchartsAsRechart(chartConfig)}
                </Box>
                {textAfterChart && renderTextContent(textAfterChart, allResults)}
                {shouldShowOutputVerificationDisclaimer(description) && (
                  <OutputVerificationDisclaimer />
                )}
              </>
            );
          }
        }
      } catch (e) {
        console.error('Failed to parse inline Highcharts config:', e);
        // Fall through to regular markdown parsing if JSON parsing fails
      }
    }

    // Regular markdown parsing
    const element = renderTextContent(description, allResults);
    if (shouldShowOutputVerificationDisclaimer(description)) {
      element.push(<OutputVerificationDisclaimer key="output-verification-disclaimer" />);
    }
    return element;
  };

  // Helper to render text content with markdown
  const renderTextContent = (text, allResults = []) => {
    const lines = text.split('\n');
    const elements = [];
    let inTable = false;
    let tableRows = [];

    lines.forEach((line, idx) => {
      // Check if it's a table row
      if (line.trim().startsWith('|')) {
        inTable = true;
        tableRows.push(line);
      } else {
        // If we were in a table and now we're not, render the table
        if (inTable && tableRows.length > 0) {
          elements.push(renderMarkdownTable(tableRows, elements.length, allResults));
          tableRows = [];
          inTable = false;
        }

        // Render regular line with bold text support
        if (line.trim()) {
          elements.push(
            <Typography 
              key={idx} 
              variant="body2" 
              sx={{ 
                mb: 1,
                fontSize: '14px',
                lineHeight: 1.6,
                color: '#303030',
              }}
            >
              {renderBoldText(line)}
            </Typography>
          );
        } else {
          // Empty line - add spacing
          elements.push(<Box key={idx} sx={{ height: '8px' }} />);
        }
      }
    });

    // Render any remaining table
    if (inTable && tableRows.length > 0) {
      elements.push(renderMarkdownTable(tableRows, elements.length, allResults));
    }

    return elements;
  };

  // Helper to convert Highcharts config to Recharts
  const renderHighchartsAsRechart = (config) => {
    try {
      const chartType = config.chart?.type || 'bar';
      const title = config.title?.text || '';
      const categories = config.xAxis?.categories || [];
      const xAxisLabel = config.xAxis?.title?.text || '';
      const yAxisLabel = config.yAxis?.title?.text || '';
      const allSeries = config.series || [];
      
      // Support multiple series
      const colors = ['#005587', '#00BFB3', '#43B02A', '#00A3E0', '#008F86', '#027223', '#FE8A12']; // IQVIA Brand Colors

      // Transform data for Recharts with multiple series support
      const chartData = categories.map((category, idx) => {
        const dataPoint = { name: category };
        allSeries.forEach((series, seriesIdx) => {
          const seriesName = series.name || `Series ${seriesIdx + 1}`;
          dataPoint[seriesName] = series.data?.[idx] || 0;
        });
        return dataPoint;
      });

      // Generate unique ID for this highchart
      const highchartId = `highchart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Prepare data for Excel download
      const headers = ['Category', ...allSeries.map(s => s.name || 'Value')];
      const excelData = categories.map((cat, idx) => [
        cat,
        ...allSeries.map(s => s.data?.[idx] || 0)
      ]);

      return (
        <Box sx={{ mt: 2, mb: 2 }}>
          {title && (
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600, 
                color: '#005587',
                fontSize: '16px',
                textAlign: 'center',
              }}
            >
              {title}
            </Typography>
          )}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 0.5,
            mb: 1,
          }}>
            <IconButton
              size="small"
              onClick={() => downloadMarkdownTableAsExcel(headers, excelData, `SiMS_Data_Exploration_${chartType}`)}
              sx={{
                backgroundColor: '#00BFB3',
                color: '#fff',
                padding: '6px',
                '&:hover': {
                  backgroundColor: '#008577',
                },
              }}
              title="Download as Excel"
            >
              <ExcelIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={async () => {
                // Create result-like object for PDF export
                const chartResult = {
                  columns: headers.map(h => ({ name: h })),
                  data: excelData
                };
                await downloadChartAsPDF(chartResult, `SiMS_Data_Exploration_${chartType}`, highchartId, getLastBotDescription());
              }}
              sx={{
                backgroundColor: '#d32f2f',
                color: '#fff',
                padding: '6px',
                '&:hover': {
                  backgroundColor: '#b71c1c',
                },
              }}
              title="Download as PDF"
            >
              <PdfIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box 
            id={highchartId}
            sx={{ 
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '24px',
              border: '1px solid #e8eaed',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}
          >
            <ChartRenderer
              chartType={chartType === 'column' || chartType === 'bar' ? 'bar' : chartType === 'line' ? 'line' : chartType === 'pie' ? 'pie' : 'bar'}
              chartData={chartData}
              xAxisKey="name"
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              colors={colors}
              allSeries={allSeries}
              chartId={highchartId}
              gridStroke="#e8eaed"
              showLegend={allSeries.length > 1}
            />
          </Box>
        </Box>
      );
    } catch (error) {
      console.error('Error rendering Highcharts config:', error);
      return (
        <Typography color="error" sx={{ mt: 2 }}>
          Failed to render chart
        </Typography>
      );
    }
  };

  // Helper to render bold text (**text**) and markdown links
  const handleSourceFileDownload = useCallback(async (fileName) => {
    try {
      const data = JSON.parse(localStorage.getItem('data') || '{}');
      const token = data?.token;
      const url = `${chatbotUrls.downloadSource}?fileName=${encodeURIComponent(fileName)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: '*/*',
        },
      });

      if (response.status === 404) {
        let message = `The file "${fileName}" does not exist.`;
        try {
          const err = await response.json();
          if (err?.message) message = err.message;
        } catch {
          // keep default message
        }
        alert(message);
        return;
      }

      if (!response.ok) {
        let message = `Unable to download "${fileName}".`;
        try {
          const err = await response.json();
          if (err?.message) message = err.message;
        } catch {
          // keep default message
        }
        alert(message);
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error downloading source file:', error);
      alert(`Unable to download "${fileName}".`);
    }
  }, []);

  const renderBoldText = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|\[.*?\]\(.*?\))/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      const linkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
      if (linkMatch) {
        const label = linkMatch[1];
        const href = linkMatch[2];
        const isSourceFile = href.startsWith('sourcefile:');
        const fileName = isSourceFile ? href.slice('sourcefile:'.length) : null;

        return (
          <a
            key={idx}
            href={isSourceFile ? '#' : href}
            target={isSourceFile ? undefined : '_blank'}
            rel={isSourceFile ? undefined : 'noopener noreferrer'}
            onClick={isSourceFile ? (e) => {
              e.preventDefault();
              handleSourceFileDownload(fileName);
            } : undefined}
            style={{ color: '#005587', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {label}
          </a>
        );
      }
      return part;
    });
  };

  // Helper to download markdown table as Excel
  const downloadMarkdownTableAsExcel = useCallback(async (headers, dataRows, fileName = 'table_data') => {
    try {
      await generateExcelFile(headers, dataRows, fileName, 'Sheet1');
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('Failed to download table as Excel. Please try again.');
    }
  }, [generateExcelFile]);

  // Helper to download markdown table as PDF
  const downloadMarkdownTableAsPDF = useCallback((headers, dataRows, fileName = 'table_data') => {
    try {
      generatePDFFile(headers, dataRows, fileName, 30);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to download table as PDF. Please try again.');
    }
  }, [generatePDFFile]);

  // Helper function to identify column type for markdown tables
  const getMarkdownColumnType = (header, sampleValue, idx) => {
    const headerLower = (header || '').toLowerCase();
    const sampleStr = String(sampleValue || '');
    
    // Check if time-like column FIRST (before numeric check)
    // Check column name for time keywords
    const isTimeColumnByName = headerLower.includes('month') || 
                              headerLower.includes('date') || 
                              headerLower.includes('quarter') || 
                              headerLower.includes('year') ||
                              headerLower.includes('week') ||
                              headerLower.includes('day') ||
                              headerLower.includes('period');
    
    // Check if value looks like a date/time
    const isTimeColumnByValue = sampleStr.match(/^\d{4}[-/]\d{2}/) || // Date format
                               sampleStr.match(/^\d{6,8}$/) || // Date code
                               (sampleStr.match(/^\d{4}$/) && headerLower.includes('year')); // 4-digit year
    
    if (isTimeColumnByName || isTimeColumnByValue) {
      return 'time';
    }
    
    // Check if numeric value (but not a time value)
    const isNumericValue = !isNaN(sampleValue) && 
                         sampleValue !== null && 
                         sampleValue !== '' 
                         &&
                         !sampleStr.match(/^\d{4}$/); // Exclude 4-digit years that might be strings
    
    if (isNumericValue) {
      return 'numeric';
    }
    
    return 'categorical';
  };

  // Helper to check if markdown table should show a chart
  const shouldShowChartForMarkdownTable = (headerCells, dataRows, allResults = []) => {
    if (!headerCells || !dataRows || dataRows.length === 0) return false;
    
    // Don't show chart if there's already a chart in the results
    if (hasChartInResults(allResults)) return false;
    
    // Need at least 2 columns
    if (headerCells.length < 2) return false;
    
    // Classify columns
    const columnTypes = headerCells.map((header, idx) => ({
      index: idx,
      name: header,
      type: getMarkdownColumnType(header, dataRows[0]?.[idx], idx)
    }));
    
    // Must have at least one numeric column (Y-axis)
    const numericColumns = columnTypes.filter(col => col.type === 'numeric');
    if (numericColumns.length === 0) return false;
    
    // Must have at least one non-numeric column (X-axis)
    const nonNumericColumns = columnTypes.filter(col => col.type !== 'numeric');
    if (nonNumericColumns.length === 0) return false;
    
    return true;
  };

  // Helper to render line chart from markdown table data
  const renderLineChartFromMarkdownTable = (headerCells, dataRows, key) => {
    if (!dataRows || dataRows.length === 0) return null;

    // Check if this chart has custom axis settings from stored config
    const chartConfigKey = `markdown-chart-${key}`;
    const customConfig = chartConfigs[chartConfigKey] || null;
    
    // Process dataRows to convert scientific notation for numeric columns
    const processedDataRows = dataRows.map(row => {
      return row.map((cell, idx) => {
        const header = headerCells[idx];
        const headerLower = (header || '').toLowerCase();
        // Check if column looks numeric (has numeric type indicators in name or value looks numeric)
        const isNumeric = getMarkdownColumnType(header, cell, idx) === 'numeric';
        return convertScientificNotation(cell, isNumeric);
      });
    });

    // Classify all columns (use processed data for type detection)
    const columnTypes = headerCells.map((header, idx) => ({
      index: idx,
      name: header,
      type: getMarkdownColumnType(header, processedDataRows[0]?.[idx], idx)
    }));
    
    // Find time columns (prioritize for X-axis)
    const timeColumns = columnTypes.filter(col => col.type === 'time');
    
    // Find numeric columns (Y-axis candidates)
    const numericColumns = columnTypes.filter(col => col.type === 'numeric');
    
    if (numericColumns.length === 0) return null;
    
    // Get all non-numeric columns (for X-axis and legend)
    const nonNumericColumns = columnTypes.filter(col => col.type !== 'numeric');
    
    // Determine X-axis: use custom if set, otherwise prioritize time columns, then first non-numeric
    let xAxisColumn;
    let xAxisIndex;
    if (customConfig && customConfig.xAxis) {
      xAxisColumn = columnTypes.find(col => col.name === customConfig.xAxis && col.type !== 'numeric');
      if (xAxisColumn) {
        xAxisIndex = xAxisColumn.index;
      } else {
        // Fallback to default logic
        if (timeColumns.length > 0) {
          xAxisColumn = timeColumns[0];
          xAxisIndex = xAxisColumn.index;
        } else if (nonNumericColumns.length > 0) {
          xAxisColumn = nonNumericColumns[0];
          xAxisIndex = xAxisColumn.index;
        } else {
          return null;
        }
      }
    } else {
      if (timeColumns.length > 0) {
        xAxisColumn = timeColumns[0];
        xAxisIndex = xAxisColumn.index;
      } else if (nonNumericColumns.length > 0) {
        xAxisColumn = nonNumericColumns[0];
        xAxisIndex = xAxisColumn.index;
      } else {
        return null; // Need at least one non-numeric column for X-axis
      }
    }
    
    const xAxisKey = xAxisColumn.name;
    
    // Determine Y-axis: handle both single value and array (multiple Y-axis)
    let yAxisColumns = [];
    let yAxisIndices = [];
    let yAxisKeys = [];
    
    if (customConfig && customConfig.yAxis) {
      const yAxisConfig = customConfig.yAxis;
      // Check if it's an array (multiple Y-axis) or single value
      if (Array.isArray(yAxisConfig)) {
        yAxisConfig.forEach(yAxisName => {
          const col = numericColumns.find(c => c.name === yAxisName);
          if (col) {
            yAxisColumns.push(col);
            yAxisIndices.push(col.index);
            yAxisKeys.push(col.name);
          }
        });
      } else {
        // Single Y-axis value
        const col = numericColumns.find(c => c.name === yAxisConfig);
        if (col) {
          yAxisColumns.push(col);
          yAxisIndices.push(col.index);
          yAxisKeys.push(col.name);
        }
      }
    }
    
    // If no Y-axis found, use default (last numeric column)
    if (yAxisColumns.length === 0) {
      const yAxisColumn = numericColumns[numericColumns.length - 1];
      yAxisColumns = [yAxisColumn];
      yAxisIndices = [yAxisColumn.index];
      yAxisKeys = [yAxisColumn.name];
    }
    
    const hasMultipleYAxis = yAxisKeys.length > 1;
    const yAxisKey = yAxisKeys[0]; // For backward compatibility
    const yAxisIndex = yAxisIndices[0]; // For backward compatibility
    
    // Determine legend/grouping: 
    // - If multiple Y-axis, legend is 'none' (Y-axis values become the series)
    // - Otherwise, use custom if set, or find second non-numeric column
    let legendColumn;
    let legendIndex;
    if (hasMultipleYAxis || (customConfig && customConfig.legend === 'none')) {
      legendIndex = -1;
    } else if (customConfig && customConfig.legend !== undefined) {
      if (customConfig.legend === 'none' || customConfig.legend === '') {
        legendIndex = -1;
      } else {
        legendColumn = nonNumericColumns.find(col => col.name === customConfig.legend && col.index !== xAxisIndex);
        legendIndex = legendColumn ? legendColumn.index : -1;
      }
    } else {
      legendColumn = nonNumericColumns.find(col => col.index !== xAxisIndex);
      legendIndex = legendColumn ? legendColumn.index : -1;
    }
    const legendKey = legendColumn ? legendColumn.name : null;

    // Determine chart type: use custom config if available, otherwise default based on time column
    const hasTimeColumn = timeColumns.length > 0;
    const defaultChartType = hasTimeColumn ? 'line' : 'bar';
    const chartType = customConfig?.chartType || defaultChartType;

    let chartData;
    let seriesKeys = [];

    // If multiple Y-axis, use them as series
    if (hasMultipleYAxis) {
      const uniqueXValues = [...new Set(processedDataRows.map(row => String(row[xAxisIndex])))].sort();
      
      chartData = uniqueXValues.map(xValue => {
        const dataPoint = { [xAxisKey]: xValue };
        
        // For each Y-axis column, get the value for this X value
        yAxisKeys.forEach((yKey, idx) => {
          const yIdx = yAxisIndices[idx];
          const matchingRows = processedDataRows.filter(r => String(r[xAxisIndex]) === String(xValue));
          
          if (matchingRows.length > 0) {
            // Sum the Y-axis values for this X value
            const sum = matchingRows.reduce((acc, row) => {
              const yValue = row[yIdx];
              const numValue = isNaN(yValue) ? 0 : Number(yValue);
              return acc + numValue;
            }, 0);
            dataPoint[yKey] = sum;
          } else {
            dataPoint[yKey] = 0;
          }
        });
        
        return dataPoint;
      });
      
      seriesKeys = yAxisKeys;
    } else if (legendIndex !== -1) {
      // Case: Has grouping column - group by X-axis and legend, aggregate Y-axis
      const uniqueGroups = [...new Set(processedDataRows.map(row => String(row[legendIndex])))];
      seriesKeys = uniqueGroups;
      
      // Get unique X-axis values
      const uniqueXValues = [...new Set(processedDataRows.map(row => String(row[xAxisIndex])))].sort();
      
      // Transform data: group by X-axis and legend, sum Y-axis values
      chartData = uniqueXValues.map(xValue => {
        const dataPoint = { [xAxisKey]: xValue };
        
        // For each grouping value, aggregate Y values
        uniqueGroups.forEach(groupValue => {
          // Find all rows matching this X and legend combination
          const matchingRows = processedDataRows.filter(r => 
            String(r[xAxisIndex]) === String(xValue) && String(r[legendIndex]) === String(groupValue)
          );
          
          if (matchingRows.length > 0) {
            // Sum the Y-axis values
            const sum = matchingRows.reduce((acc, row) => {
              const yValue = row[yAxisIndex];
              const numValue = isNaN(yValue) ? 0 : Number(yValue);
              return acc + numValue;
            }, 0);
            dataPoint[groupValue] = sum;
          } else {
            dataPoint[groupValue] = null;
          }
        });
        
        return dataPoint;
      });
    } else {
      // Case: No grouping column - group by X-axis, aggregate Y-axis
      const uniqueXValues = [...new Set(processedDataRows.map(row => String(row[xAxisIndex])))].sort();
      
      chartData = uniqueXValues.map(xValue => {
        const dataPoint = { [xAxisKey]: xValue };
        
        // Find all rows matching this X value
        const matchingRows = processedDataRows.filter(r => String(r[xAxisIndex]) === String(xValue));
        
        // Sum the Y-axis values for this X value
        const sum = matchingRows.reduce((acc, row) => {
          const yValue = row[yAxisIndex];
          const numValue = isNaN(yValue) ? 0 : Number(yValue);
          return acc + numValue;
        }, 0);
        
        dataPoint[yAxisKey] = sum;
        
        return dataPoint;
      });
      
      seriesKeys = [yAxisKey];
    }

    const colors = ['#005587', '#00BFB3', '#43B02A', '#00A3E0', '#008F86', '#027223', '#FE8A12']; // IQVIA Brand Colors
    const xAxisLabel = xAxisKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const yAxisLabel = yAxisKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const chartId = `markdown-table-chart-${key}`;

    // Create result-like object for download functions
    const chartResult = {
      columns: headerCells.map(h => ({ name: h })),
      data: processedDataRows,
      chartMetadata: { chartType: chartType }
    };

    return (
      <Box sx={{ mt: 3 }}>
        <Box className="result-header" sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ChartIcon sx={{ fontSize: 20, color: '#00A3E0', mr: 1.5 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#005587', fontSize: '14px' }}>
              {chartType === 'bar' ? 'Bar Chart' : 
               chartType === 'line' ? 'Line Chart' :
               chartType === 'area' ? 'Area Chart' :
               chartType === 'scatter' ? 'Scatter Chart' :
               chartType === 'pie' ? 'Pie Chart' :
               chartType === 'radialbar' ? 'Radial Bar Chart' : 'Chart'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              // Prepare available columns for selection
              const availableColumns = headerCells.map((header, idx) => ({
                name: header,
                type: getMarkdownColumnType(header, dataRows[0]?.[idx], idx),
                index: idx
              }));
              
              // Get saved configuration if exists
              const chartConfigKey = `markdown-chart-${key}`;
              const savedConfig = chartConfigs[chartConfigKey] || null;
              
              // For pie/radialbar: legend = xAxis (labels), value = yAxis (data)
              // For others: xAxis, yAxis, legend as normal
              const isPieOrRadialBar = chartType === 'pie' || chartType === 'radialbar';
              
              // Use saved config values if available, otherwise use calculated defaults
              const savedChartType = savedConfig?.chartType || chartType;
              const isPieOrRadialBarSaved = savedChartType === 'pie' || savedChartType === 'radialbar';
              
              setChartEditDialog({
                open: true,
                chartId: chartConfigKey,
                chartType: 'markdown',
                originalData: { headerCells, dataRows },
                xAxis: isPieOrRadialBarSaved ? '' : (savedConfig?.xAxis || xAxisKey),
                yAxis: isPieOrRadialBarSaved ? [] : (
                  savedConfig?.yAxis 
                    ? (Array.isArray(savedConfig.yAxis) ? savedConfig.yAxis : [savedConfig.yAxis])
                    : (yAxisKey ? [yAxisKey] : [])
                ),
                legend: isPieOrRadialBarSaved 
                  ? (savedConfig?.legend || xAxisKey)
                  : (savedConfig?.legend && savedConfig.legend !== 'none' ? savedConfig.legend : (legendKey || '')),
                value: isPieOrRadialBarSaved ? (savedConfig?.value || yAxisKey) : '',
                selectedChartType: savedChartType,
                availableColumns: availableColumns,
              });
            }}
            sx={{
              backgroundColor: '#00A3E0',
              color: '#fff',
              padding: '6px',
              '&:hover': {
                backgroundColor: '#005587',
              },
            }}
            title="Edit Chart"
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Paper className="chart-result-container" sx={{ p: 3 }}>
          <Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 0.5,
              mb: 1,
            }}>
              <IconButton
                size="small"
                onClick={() => downloadChartAsExcel(chartResult, `SiMS_Data_Exploration_${chartType}_chart_${key}`)}
                sx={{
                  backgroundColor: '#00BFB3',
                  color: '#fff',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: '#008577',
                  },
                }}
                title="Download as Excel"
              >
                <ExcelIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={async () => await downloadChartAsPDF(chartResult, `SiMS_Data_Exploration_${chartType}_chart_${key}`, chartId, getLastBotDescription())}
                sx={{
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  },
                }}
                title="Download as PDF"
              >
                <PdfIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box id={chartId}>
              {chartType === 'pie' || chartType === 'radialbar' ? (() => {
                const pieValueKey = customConfig?.value || yAxisKey;
                const pieLabelKey = customConfig?.legend || xAxisKey;
                
                // Convert processedDataRows to objects
                const rawPieData = processedDataRows.map(row => {
                  const rowObj = {};
                  headerCells.forEach((header, idx) => {
                    rowObj[header] = row[idx];
                  });
                  return rowObj;
                });
                
                // Aggregate data by legend column (pieLabelKey) for pie charts
                const aggregatedPieDataMap = new Map();
                rawPieData.forEach(item => {
                  const label = item[pieLabelKey];
                  const value = item[pieValueKey];
                  
                  if (label !== null && label !== undefined) {
                    const labelStr = String(label);
                    const numValue = typeof value === 'number' ? value : parseFloat(value);
                    const validValue = isNaN(numValue) ? 0 : numValue;
                    
                    if (aggregatedPieDataMap.has(labelStr)) {
                      aggregatedPieDataMap.set(labelStr, aggregatedPieDataMap.get(labelStr) + validValue);
                    } else {
                      aggregatedPieDataMap.set(labelStr, validValue);
                    }
                  }
                });
                
                // Convert map back to array format
                const aggregatedPieData = Array.from(aggregatedPieDataMap.entries()).map(([name, value]) => ({
                  [pieLabelKey]: name,
                  [pieValueKey]: value
                }));
                
                return (
                  <ChartRenderer
                    chartType={chartType}
                    chartData={aggregatedPieData}
                    pieDataKey={pieValueKey}
                    pieNameKey={pieLabelKey}
                    chartId={chartId}
                  />
                );
              })() : (
                <ChartRenderer
                  chartType={chartType}
                  chartData={chartData}
                  xAxisKey={xAxisKey}
                  seriesKeys={seriesKeys}
                  xAxisLabel={xAxisLabel}
                  yAxisLabel={yAxisLabel}
                  colors={colors}
                  chartId={chartId}
                  xAxisLabelStyle={{ fontSize: 13, fontWeight: 600 }}
                  yAxisLabelStyle={{ fontSize: 13, fontWeight: 600 }}
                  showLegend={seriesKeys.length > 1}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  // Helper to render markdown tables
  const renderMarkdownTable = (tableLines, key, allResults = []) => {
    // Remove separator line (the one with dashes)
    const dataLines = tableLines.filter(line => !line.includes('---'));
    
    if (dataLines.length === 0) return null;

    // Parse header
    const headerCells = dataLines[0]
      .split('|')
      .map(cell => cell.trim())
      .filter(cell => cell);

    // Parse data rows
    const dataRows = dataLines.slice(1).map(line =>
      line
        .split('|')
        .map(cell => cell.trim())
        .filter(cell => cell)
    );

    // Pagination state for this specific markdown table
    const page = markdownTablePagination[key]?.page || 0;
    const rowsPerPage = markdownTablePagination[key]?.rowsPerPage || 20;
    const paginatedRows = dataRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Box key={key}>
        <TableRenderer
          columns={headerCells}
          data={dataRows}
          uniqueKey={key}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(event, newPage) => handleMarkdownTableChangePage(key, newPage)}
          onRowsPerPageChange={(event) => handleMarkdownTableChangeRowsPerPage(key, event)}
          onDownloadExcel={downloadMarkdownTableAsExcel}
          onDownloadPDF={downloadMarkdownTableAsPDF}
          downloadData={{ headers: headerCells, dataRows: dataRows }}
          maxHeight={400}
          paperSx={{ mb: 2 }}
          headerCellSx={{
            fontSize: '12px',
            padding: '10px 12px',
          }}
          bodyCellSx={{
            padding: '8px 12px',
          }}
        />
        {/* Show line chart if conditions are met */}
        {shouldShowChartForMarkdownTable(headerCells, dataRows, allResults) && 
          renderLineChartFromMarkdownTable(headerCells, dataRows, key)}
      </Box>
    );
  };

  // Custom Tooltip Component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e8eaed',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Typography sx={{ fontWeight: 600, color: '#005587', fontSize: '13px', mb: 0.5 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} sx={{ color: '#303030', fontSize: '13px' }}>
              {entry.name}: <strong>{entry.value?.toLocaleString()}</strong>
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderChart = (result, chartId, messageId = null) => {
    if (!result.data || result.data.length === 0) {
      return <Typography>No data available for chart</Typography>;
    }

    // Create unique chart ID
    const uniqueChartId = messageId ? `chart-${messageId}-${chartId}` : `chart-${chartId}`;
    
    // Check if this chart has custom axis settings from stored config
    const customConfig = chartConfigs[uniqueChartId] || null;

    // Transform data for charts - convert scientific notation for numeric columns
    const rawChartData = result.data.map((row) => {
      const obj = {};
      result.columns.forEach((col, idx) => {
        const cellValue = row[idx];
        const isNumeric = col.type && (
          col.type.toUpperCase().includes('INT') ||
          col.type.toUpperCase().includes('DOUBLE') ||
          col.type.toUpperCase().includes('FLOAT') ||
          col.type.toUpperCase().includes('DECIMAL') ||
          col.type.toUpperCase().includes('NUMERIC')
        );
        
        // Convert scientific notation first; keep text columns as strings
        const convertedValue = convertScientificNotation(cellValue, isNumeric);
        if (isNumeric) {
          const numValue = Number(convertedValue);
          obj[col.name] = Number.isNaN(numValue) ? 0 : numValue;
        } else {
          obj[col.name] = convertedValue ?? '';
        }
      });
      return obj;
    });

    const chartType = result.chartMetadata?.chartType || 'bar';
    const chartTitle = result.chartMetadata?.title || '';
    
    // Classify all columns (use processed data for type detection)
    const columnTypes = result.columns.map((col, idx) => ({
      index: idx,
      name: col.name,
      type: getColumnType(col, rawChartData[0]?.[col.name], idx)
    }));
    
    // Find numeric columns (Y-axis candidates)
    const numericColumns = columnTypes.filter(col => col.type === 'numeric');
    const timeColumns = columnTypes.filter(col => col.type === 'time');
    const categoricalColumns = columnTypes.filter(col => col.type === 'categorical');

    // Get all non-numeric columns (for X-axis and legend)
    const nonNumericColumns = columnTypes.filter(col => col.type !== 'numeric');

    const bestTimeColumn = selectBestTimeColumn(timeColumns, rawChartData);
    const isStackedGroupBarChart = detectStackedGroupChart(chartTitle);
    const isRankingBarChart = !isStackedGroupBarChart && chartTitleWantsRankingBar(chartTitle);
    
    // Determine X-axis: prioritize finest-grained time column (Period over Year)
    let xAxisKey;
    let xAxisIndex;
    if (customConfig && customConfig.xAxis) {
      const xAxisCol = columnTypes.find(col => col.name === customConfig.xAxis && col.type !== 'numeric');
      if (xAxisCol) {
        xAxisKey = xAxisCol.name;
        xAxisIndex = xAxisCol.index;
      } else if (bestTimeColumn) {
        xAxisKey = bestTimeColumn.name;
        xAxisIndex = bestTimeColumn.index;
      } else {
        xAxisKey = nonNumericColumns.length > 0 ? nonNumericColumns[0].name : result.columns[0]?.name;
        xAxisIndex = columnTypes.find(col => col.name === xAxisKey)?.index || 0;
      }
    } else if (result.columns.length === 2) {
      xAxisKey = result.columns[0].name;
      xAxisIndex = 0;
    } else if (!isRankingBarChart && bestTimeColumn) {
      xAxisKey = bestTimeColumn.name;
      xAxisIndex = bestTimeColumn.index;
    } else {
      xAxisKey = nonNumericColumns.length > 0 ? nonNumericColumns[0].name : result.columns[0]?.name;
      xAxisIndex = columnTypes.find(col => col.name === xAxisKey)?.index || 0;
    }
    
    // Determine Y-axis: handle both single value and array (multiple Y-axis)
    let yAxisKeys = [];
    if (customConfig && customConfig.yAxis) {
      const yAxisConfig = customConfig.yAxis;
      // Check if it's an array (multiple Y-axis) or single value
      if (Array.isArray(yAxisConfig)) {
        yAxisConfig.forEach(yAxisName => {
          const col = numericColumns.find(c => c.name === yAxisName);
          if (col) {
            yAxisKeys.push(col.name);
          }
        });
      } else {
        // Single Y-axis value
        const col = numericColumns.find(c => c.name === yAxisConfig);
        if (col) {
          yAxisKeys = [col.name];
        } else {
          // Fallback to default
          yAxisKeys = numericColumns.length > 0 ? [numericColumns[numericColumns.length - 1].name] : [result.columns[result.columns.length - 1]?.name];
        }
      }
    } else {
      // Default logic: one primary measure, not every numeric/ID column
      if (result.columns.length === 2) {
        yAxisKeys = [result.columns[1].name];
      } else {
        const primaryMeasure = selectPrimaryMeasureColumn(numericColumns, chartTitle);
        yAxisKeys = primaryMeasure
          ? [primaryMeasure.name]
          : (numericColumns.length > 0
            ? [numericColumns[numericColumns.length - 1].name]
            : [result.columns[result.columns.length - 1]?.name]);
      }
    }
    
    const hasMultipleYAxis = yAxisKeys.length > 1;
    
    // Determine legend/grouping: use custom if set, otherwise second dimension for trend charts
    let legendKey = null;
    let legendIndex = -1;
    if (!hasMultipleYAxis && customConfig && customConfig.legend === 'none') {
      legendIndex = -1;
    } else if (!hasMultipleYAxis && customConfig && customConfig.legend) {
      const legendCol = nonNumericColumns.find(col => col.name === customConfig.legend && col.index !== xAxisIndex);
      if (legendCol) {
        legendKey = legendCol.name;
        legendIndex = legendCol.index;
      }
    } else if (!hasMultipleYAxis && isStackedGroupBarChart) {
      const timeCol = selectBestTimeColumn(timeColumns, rawChartData);
      const seriesCol = selectStackedGroupSeriesColumn(categoricalColumns, timeCol?.name, rawChartData);

      if (timeCol) {
        xAxisKey = timeCol.name;
        xAxisIndex = timeCol.index;
      }
      if (seriesCol) {
        legendKey = seriesCol.name;
        legendIndex = seriesCol.index;
      }
    } else if (!hasMultipleYAxis && !isRankingBarChart && (chartType === 'line' || chartType === 'bar') && bestTimeColumn) {
      const legendCol = selectLegendColumnForTrend(
        columnTypes.filter(col => col.type === 'categorical'),
        xAxisKey,
        yAxisKeys,
        rawChartData
      );
      if (legendCol) {
        legendKey = legendCol.name;
        legendIndex = legendCol.index;
      }
    } else if (!hasMultipleYAxis && chartType === 'bar' && !isRankingBarChart) {
      const categoricalColumns = columnTypes.filter(col => col.type === 'categorical');
      if (categoricalColumns.length >= 2) {
        const countryPattern = /country|geo|region|market|territory/i;
        const productPattern = /product|trade|brand|sku|item|original_trade/i;
        const countryCol = categoricalColumns.find(col => countryPattern.test(col.name));
        const productCol = categoricalColumns.find(col => productPattern.test(col.name));

        if (countryCol && productCol) {
          xAxisKey = countryCol.name;
          xAxisIndex = countryCol.index;
          legendKey = productCol.name;
          legendIndex = productCol.index;
        } else {
          const ranked = categoricalColumns
            .map(col => ({
              col,
              count: new Set(rawChartData.map(row => String(row[col.name]))).size
            }))
            .sort((a, b) => a.count - b.count);

          if (ranked.length >= 2) {
            xAxisKey = ranked[0].col.name;
            xAxisIndex = ranked[0].col.index;
            legendKey = ranked[1].col.name;
            legendIndex = ranked[1].col.index;
          }
        }
      }
    }

    let rankingFamilyKey = null;
    if (isRankingBarChart) {
      const labelCol =
        categoricalColumns.find(col => /description_item|item_description|description/i.test(col.name) && !/family|product/i.test(col.name)) ||
        categoricalColumns
          .map(col => ({
            col,
            avgLen: rawChartData.reduce((sum, row) => sum + String(row[col.name] ?? '').length, 0) / rawChartData.length,
          }))
          .sort((a, b) => b.avgLen - a.avgLen)[0]?.col ||
        categoricalColumns.find(col => !/family|product|variant|code/i.test(col.name));

      const familyCol = categoricalColumns.find(col => /family|product_family|variant/i.test(col.name));

      if (labelCol) {
        xAxisKey = labelCol.name;
        xAxisIndex = labelCol.index;
      }
      rankingFamilyKey = familyCol?.name || null;
      legendKey = null;
      legendIndex = -1;
    }

    // Align chart type with Databricks intent from title/metadata
    let effectiveChartType = chartType;
    if (chartType === 'line' || chartType === 'bar') {
      if (isStackedGroupBarChart) {
        effectiveChartType = 'bar';
      } else if (chartTitleWantsChangeMeasure(chartTitle) || isRankingBarChart) {
        effectiveChartType = 'bar';
      } else if (chartTitleWantsTrendLine(chartTitle) || (legendKey && bestTimeColumn)) {
        effectiveChartType = 'line';
      } else if (bestTimeColumn && !legendKey) {
        effectiveChartType = chartType === 'line' ? 'line' : 'bar';
      }
    }
    
    // Transform data based on legend grouping if needed
    let chartData = rawChartData;
    let seriesKeys = yAxisKeys;
    let rankingBarColors = null;

    const truncateChartLabel = (value, maxLength = 40) => {
      const text = String(value ?? '');
      return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;
    };

    if (isRankingBarChart) {
      const measureKey = yAxisKeys[0];
      chartData = [...rawChartData]
        .sort((a, b) => (Number(b[measureKey]) || 0) - (Number(a[measureKey]) || 0))
        .map(row => ({
          ...row,
          [xAxisKey]: truncateChartLabel(row[xAxisKey]),
        }));
      seriesKeys = [measureKey];

      if (rankingFamilyKey) {
        const familyPalette = ['#007DC3', '#E91E63', '#008F86', '#6A1B9A', '#43B02A', '#D62828', '#1982C4', '#F77F00'];
        const familyColorMap = {};
        let paletteIndex = 0;
        rankingBarColors = chartData.map(row => {
          const family = String(row[rankingFamilyKey] ?? 'Unknown');
          if (!familyColorMap[family]) {
            familyColorMap[family] = familyPalette[paletteIndex % familyPalette.length];
            paletteIndex += 1;
          }
          return familyColorMap[family];
        });
      }
    } else if (hasMultipleYAxis) {
      const uniqueXValues = sortChartAxisValues([...new Set(rawChartData.map(row => String(row[xAxisKey])))]);
      
      chartData = uniqueXValues.map(xValue => {
        const dataPoint = { [xAxisKey]: xValue };
        
        // For each Y-axis column, get the value for this X value
        yAxisKeys.forEach(yKey => {
          const matchingRows = rawChartData.filter(r => String(r[xAxisKey]) === String(xValue));
          
          if (matchingRows.length > 0) {
            // Sum the Y-axis values for this X value
            const sum = matchingRows.reduce((acc, row) => {
              const yValue = row[yKey];
              const numValue = isNaN(yValue) ? 0 : Number(yValue);
              return acc + numValue;
            }, 0);
            dataPoint[yKey] = sum;
          } else {
            dataPoint[yKey] = 0;
          }
        });
        
        return dataPoint;
      });
      
      seriesKeys = yAxisKeys;
    } else if (legendIndex !== -1 && legendKey && !yAxisKeys.includes(legendKey)) {
      // Group by X-axis and legend, aggregate Y-axis values
      let uniqueGroups = [...new Set(rawChartData.map(row => String(row[legendKey])))];
      if (effectiveChartType === 'line' || effectiveChartType === 'bar') {
        const activeGroups = uniqueGroups.filter(groupValue =>
          rawChartData.some(row => {
            if (String(row[legendKey]) !== groupValue) return false;
            const yValue = row[yAxisKeys[0]];
            const numValue = typeof yValue === 'number' ? yValue : parseFloat(yValue);
            return !Number.isNaN(numValue) && numValue > 0;
          })
        );
        if (activeGroups.length > 0) {
          uniqueGroups = activeGroups;
        }
      }

      const groupLabelMap = new Map();
      uniqueGroups.forEach(groupValue => {
        groupLabelMap.set(
          groupValue,
          isStackedGroupBarChart ? truncateChartLabel(groupValue, 28) : groupValue
        );
      });
      seriesKeys = uniqueGroups.map(groupValue => groupLabelMap.get(groupValue));
      
      const uniqueXValues = sortChartAxisValues([...new Set(rawChartData.map(row => String(row[xAxisKey])))]);
      
      chartData = uniqueXValues.map(xValue => {
        const dataPoint = { [xAxisKey]: xValue };
        
        uniqueGroups.forEach(groupValue => {
          const seriesLabel = groupLabelMap.get(groupValue);
          const matchingRows = rawChartData.filter(r => 
            String(r[xAxisKey]) === String(xValue) && String(r[legendKey]) === String(groupValue)
          );
          
          if (matchingRows.length > 0) {
            const sum = matchingRows.reduce((acc, row) => {
              const yValue = row[yAxisKeys[0]];
              const numValue = isNaN(yValue) ? 0 : Number(yValue);
              return acc + numValue;
            }, 0);
            dataPoint[seriesLabel] = sum;
          } else {
            dataPoint[seriesLabel] = null;
          }
        });
        
        return dataPoint;
      });
    }
    
    // For backward compatibility, keep single yAxisKey for pie charts
    const yAxisKey = yAxisKeys[0];

    if (!isRankingBarChart && bestTimeColumn && xAxisKey === bestTimeColumn.name) {
      chartData = chartData.map(row => ({
        ...row,
        [xAxisKey]: formatChartPeriodLabel(row[xAxisKey]),
      }));
    }

    let averageLine = null;
    if (effectiveChartType === 'line' && chartTitleWantsTrendLine(chartTitle) && seriesKeys.length === 1 && !legendKey) {
      const values = chartData
        .map(row => row[seriesKeys[0]])
        .filter(v => typeof v === 'number' && !Number.isNaN(v));
      if (values.length) {
        averageLine = values.reduce((sum, value) => sum + value, 0) / values.length;
      }
    }

    if (effectiveChartType === 'pie' || effectiveChartType === 'radialbar') {
      // For pie/radialbar: use value and legend from config if available
      const pieValueKey = customConfig?.value || yAxisKey;
      const pieLabelKey = customConfig?.legend || xAxisKey;
      
      // Aggregate data by legend column (pieLabelKey) for pie charts
      let aggregatedPieData = rawChartData;
      if (pieLabelKey && pieValueKey) {
        const aggregatedMap = new Map();
        
        rawChartData.forEach(item => {
          const label = item[pieLabelKey];
          const value = item[pieValueKey];
          
          if (label !== null && label !== undefined) {
            const labelStr = String(label);
            const numValue = typeof value === 'number' ? value : parseFloat(value);
            const validValue = isNaN(numValue) ? 0 : numValue;
            
            if (aggregatedMap.has(labelStr)) {
              aggregatedMap.set(labelStr, aggregatedMap.get(labelStr) + validValue);
            } else {
              aggregatedMap.set(labelStr, validValue);
            }
          }
        });
        
        // Convert map back to array format
        aggregatedPieData = Array.from(aggregatedMap.entries()).map(([name, value]) => ({
          [pieLabelKey]: name,
          [pieValueKey]: value
        }));
      }
      
      return (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 0.5,
            mb: 1,
          }}>
            <IconButton
              size="small"
              onClick={() => downloadChartAsExcel(result, `${chartType}_chart_${chartId}`)}
              sx={{
                backgroundColor: '#00BFB3',
                color: '#fff',
                padding: '6px',
                '&:hover': {
                  backgroundColor: '#008577',
                },
              }}
              title="Download as Excel"
            >
              <ExcelIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={async () => await downloadChartAsPDF(result, `${chartType}_chart_${chartId}`, uniqueChartId, getLastBotDescription())}
              sx={{
                backgroundColor: '#d32f2f',
                color: '#fff',
                padding: '6px',
                '&:hover': {
                  backgroundColor: '#b71c1c',
                },
              }}
              title="Download as PDF"
            >
              <PdfIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box id={uniqueChartId}>
            <ChartRenderer
              chartType={effectiveChartType}
              chartData={aggregatedPieData}
              pieDataKey={pieValueKey}
              pieNameKey={pieLabelKey}
              chartId={uniqueChartId}
            />
          </Box>
        </Box>
      );
    } else if (effectiveChartType === 'line') {
      const colors = ['#005587', '#F77F00', '#43B02A', '#00A3E0', '#008F86', '#027223', '#FE8A12']; // IQVIA Brand Colors
      const xAxisLabel = xAxisKey ? xAxisKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';
      const yAxisLabel = yAxisKeys.length === 1
        ? yAxisKeys[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        : 'Value';
      
      return (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 0.5,
            mb: 1,
          }}>
            <IconButton
              size="small"
              onClick={() => downloadChartAsExcel(result, `line_chart_${chartId}`)}
              sx={{
                backgroundColor: '#00BFB3',
                color: '#fff',
                padding: '6px',
                '&:hover': {
                  backgroundColor: '#008577',
                },
              }}
              title="Download as Excel"
            >
              <ExcelIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={async () => await downloadChartAsPDF(result, `line_chart_${chartId}`, uniqueChartId, getLastBotDescription())}
              sx={{
                backgroundColor: '#d32f2f',
                color: '#fff',
                padding: '6px',
                '&:hover': {
                  backgroundColor: '#b71c1c',
                },
              }}
              title="Download as PDF"
            >
              <PdfIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box id={uniqueChartId} key={uniqueChartId}>
            <ChartRenderer
              chartType="line"
              chartData={chartData}
              xAxisKey={xAxisKey}
              seriesKeys={seriesKeys}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              colors={colors}
              legendKey={legendKey}
              chartId={uniqueChartId}
              averageLine={averageLine}
              xAxisLabelStyle={{ fontSize: 13, fontWeight: 600 }}
              yAxisLabelStyle={{ fontSize: 13, fontWeight: 600 }}
            />
          </Box>
        </Box>
      );
    } else {
      // Default to bar chart
      const colors = ['#F77F00', '#00A3E0', '#005587', '#43B02A', '#D62828', '#6A4C93', '#FE8A12'];
      const useStackedBar = Boolean(isStackedGroupBarChart);
      const useHorizontalBar = !useStackedBar && (
        isRankingBarChart ||
        (result.columns.length === 2 && columnTypes[0]?.type !== 'numeric') ||
        (chartData.length <= 20 &&
          chartData.some(row => String(row[xAxisKey] ?? '').length > 12))
      );
      const showBarLegend = Boolean(useStackedBar && legendKey && seriesKeys.length > 1);
      const xAxisLabel = xAxisKey ? xAxisKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';
      const yAxisLabel = yAxisKeys.length === 1
        ? yAxisKeys[0].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        : 'Value';
      
      return (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 0.5,
            mb: 1,
          }}>
            <IconButton
              size="small"
              onClick={() => downloadChartAsExcel(result, `bar_chart_${chartId}`)}
              sx={{
                backgroundColor: '#00BFB3',
                color: '#fff',
                padding: '6px',
                '&:hover': {
                  backgroundColor: '#008577',
                },
              }}
              title="Download as Excel"
            >
              <ExcelIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={async () => await downloadChartAsPDF(result, `bar_chart_${chartId}`, uniqueChartId, getLastBotDescription())}
              sx={{
                backgroundColor: '#d32f2f',
                color: '#fff',
                padding: '6px',
                '&:hover': {
                  backgroundColor: '#b71c1c',
                },
              }}
              title="Download as PDF"
            >
              <PdfIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
          <Box id={uniqueChartId} key={uniqueChartId}>
            <ChartRenderer
              chartType="bar"
              chartData={chartData}
              xAxisKey={xAxisKey}
              seriesKeys={seriesKeys}
              xAxisLabel={xAxisLabel}
              yAxisLabel={yAxisLabel}
              colors={colors}
              legendKey={legendKey}
              chartId={uniqueChartId}
              stacked={useStackedBar}
              horizontal={useHorizontalBar}
              barColors={rankingBarColors}
              showLegend={showBarLegend}
              xAxisLabelStyle={{ fontSize: 13, fontWeight: 600 }}
              yAxisLabelStyle={{ fontSize: 13, fontWeight: 600 }}
            />
          </Box>
        </Box>
      );
    }
  };

  // Helper function to check if there's already a chart in the results
  const hasChartInResults = (results) => {
    if (!results || !Array.isArray(results)) return false;
    return results.some(result => result.visualizationType === 'chart');
  };

  const MEASURE_COLUMN_PATTERN = /demand|sales|volume|units|revenue|amount|total|count|qty|quantity|value|forecast/i;
  const CHANGE_MEASURE_PATTERN = /change|mom|pct|percent|volatility|growth|delta/i;
  const IDENTIFIER_COLUMN_PATTERN = /item\s*code|item_code|(^|_)code($|_)|^id$|_id$|sku|product_id/i;
  const INDEX_COLUMN_PATTERN = /index|relative|offset|rank|row_num|rownum/i;
  const PRODUCT_LEGEND_PATTERN = /product|variant|trade|brand|sku|original_trade/i;
  const YEAR_COLUMN_PATTERN = /^year$/i;

  const getColumnUniqueCount = (colName, data) =>
    new Set(data.map(row => String(row[colName]))).size;

  const isIndexLikeColumn = (col, data) => {
    const name = (col.name || '').toLowerCase();
    if (INDEX_COLUMN_PATTERN.test(name)) return true;

    const values = data.map(row => row[col.name]).filter(v => v !== null && v !== undefined && v !== '');
    if (!values.length) return false;

    const nums = values.map(v => Number(v)).filter(n => !Number.isNaN(n));
    if (nums.length !== values.length) return false;

    return nums.every(n => n < 0) || (nums.every(n => Number.isInteger(n)) && nums.every(n => Math.abs(n) <= 100));
  };

  const looksLikeDateValue = (value) => {
    const str = String(value ?? '').trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(str) ||
      /^\d{4}-\d{2}$/.test(str) ||
      /^\d{6}$/.test(str) ||
      /^[A-Za-z]{3}-\d{4}$/.test(str) ||
      /^[A-Za-z]{3}\s+\d{1,2},\s+\d{4}$/.test(str);
  };

  const formatChartPeriodLabel = (value) => {
    const str = String(value ?? '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const d = new Date(`${str}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    }
    if (/^\d{4}-\d{2}$/.test(str)) {
      const d = new Date(`${str}-01T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
    }
    if (/^[A-Za-z]{3}-\d{4}$/.test(str)) {
      const [monthPart, yearPart] = str.split('-');
      const d = new Date(`${monthPart} 1, ${yearPart}`);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
    }
    return str;
  };

  const GROUPED_CHART_TITLE_PATTERN = /by\s+month|month\s+and|and\s+material|delivery\s+month|per\s+month|each\s+month|breakdown\s+by\s+product|demand\s+breakdown/i;
  const RANKING_CHART_TITLE_PATTERN = /\btop\s*\d+\b|\btop\s+\d+|\bmost\b|\bhighest\b|\blowest\b|\branking\b|\bdistribution\b/i;
  const SIMPLE_CATEGORY_BAR_PATTERN = /\bby\s+(product\s+family|family|category|country|region|segment|type)\b/i;
  const MATERIAL_LEGEND_PATTERN = /material|short_text|short text|description|item|sku|^product$/i;

  const chartTitleWantsChangeMeasure = (chartTitle = '') =>
    /month-over-month|month over month|\bmom\b|change\s*\(%\)|change %|growth\s+rate|volatility|percent/i.test(chartTitle);

  const chartTitleWantsTrendLine = (chartTitle = '') =>
    /\btrend\b|over time|time series|\bmonthly\b|last\s+\d+\s+months?/i.test(chartTitle);

  const chartTitleWantsStackedGroupBar = (chartTitle = '') =>
    GROUPED_CHART_TITLE_PATTERN.test(chartTitle);

  const chartTitleWantsSimpleCategoryBar = (chartTitle = '') =>
    SIMPLE_CATEGORY_BAR_PATTERN.test(chartTitle);

  const chartTitleWantsRankingBar = (chartTitle = '') => {
    if (chartTitleWantsStackedGroupBar(chartTitle)) return false;
    if (chartTitleWantsTrendLine(chartTitle) || chartTitleWantsChangeMeasure(chartTitle)) return false;
    if (RANKING_CHART_TITLE_PATTERN.test(chartTitle)) return true;
    return chartTitleWantsSimpleCategoryBar(chartTitle);
  };

  const selectStackedGroupSeriesColumn = (categoricalCols, timeColName, data) => {
    const candidates = categoricalCols.filter(col => col.name !== timeColName);
    const materialCol = candidates.find(col => MATERIAL_LEGEND_PATTERN.test(col.name));
    if (materialCol) return materialCol;

    return [...candidates].sort(
      (a, b) => getColumnUniqueCount(b.name, data) - getColumnUniqueCount(a.name, data)
    )[0] || null;
  };

  const detectStackedGroupChart = (chartTitle) => chartTitleWantsStackedGroupBar(chartTitle);

  const METADATA_DATE_COLUMN_PATTERN = /earliest|latest|_min|_max|min_|max_/i;

  const selectBestTimeColumn = (timeCols, data) => {
    if (!timeCols.length) return null;

    const eligible = timeCols.filter(col => !isIndexLikeColumn(col, data));
    const candidates = eligible.length ? eligible : timeCols;

    return [...candidates].sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();

      const aHasDate = /date/.test(nameA);
      const bHasDate = /date/.test(nameB);
      if (aHasDate && !bHasDate) return -1;
      if (!aHasDate && bHasDate) return 1;

      const aSample = data[0]?.[a.name];
      const bSample = data[0]?.[b.name];
      const aLooksLikeDate = looksLikeDateValue(aSample);
      const bLooksLikeDate = looksLikeDateValue(bSample);
      if (aLooksLikeDate && !bLooksLikeDate) return -1;
      if (!aLooksLikeDate && bLooksLikeDate) return 1;

      const aIsYear = YEAR_COLUMN_PATTERN.test(a.name);
      const bIsYear = YEAR_COLUMN_PATTERN.test(b.name);
      if (aIsYear && !bIsYear) return 1;
      if (!aIsYear && bIsYear) return -1;

      const countA = getColumnUniqueCount(a.name, data);
      const countB = getColumnUniqueCount(b.name, data);
      return countB - countA;
    })[0];
  };

  const selectPrimaryMeasureColumn = (numericCols, chartTitle = '') => {
    if (!numericCols.length) return null;

    if (chartTitleWantsChangeMeasure(chartTitle)) {
      const changeCol = numericCols.find(col => CHANGE_MEASURE_PATTERN.test(col.name));
      if (changeCol) return changeCol;
    }

    const trendCandidates = chartTitleWantsChangeMeasure(chartTitle)
      ? numericCols
      : numericCols.filter(col => !CHANGE_MEASURE_PATTERN.test(col.name));

    const measures = trendCandidates.filter(
      col => MEASURE_COLUMN_PATTERN.test(col.name) && !IDENTIFIER_COLUMN_PATTERN.test(col.name)
    );
    if (measures.length) return measures[measures.length - 1];

    const nonIdentifiers = trendCandidates.filter(col => !IDENTIFIER_COLUMN_PATTERN.test(col.name));
    if (nonIdentifiers.length) return nonIdentifiers[nonIdentifiers.length - 1];

    return trendCandidates[trendCandidates.length - 1] || numericCols[numericCols.length - 1];
  };

  const isValidLegendColumn = (col, xAxisKey, yAxisKeys, data) => {
    if (col.name === xAxisKey || yAxisKeys.includes(col.name)) return false;
    if (YEAR_COLUMN_PATTERN.test(col.name)) return false;
    const uniqueCount = getColumnUniqueCount(col.name, data);
    if (uniqueCount <= 1 || uniqueCount >= data.length) return false;
    return true;
  };

  const selectLegendColumnForTrend = (categoricalCols, xAxisKey, yAxisKeys, data) => {
    const productCol = categoricalCols.find(
      col => PRODUCT_LEGEND_PATTERN.test(col.name) && isValidLegendColumn(col, xAxisKey, yAxisKeys, data)
    );
    if (productCol) return productCol;
    return categoricalCols.find(col => isValidLegendColumn(col, xAxisKey, yAxisKeys, data)) || null;
  };

  const parseMonthYearSortKey = (value) => {
    const match = String(value).match(/^([A-Za-z]{3})-(\d{4})$/);
    if (!match) return null;
    const parsed = new Date(`${match[1]} 1, ${match[2]}`).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  };

  const sortChartAxisValues = (values) => [...values].sort((a, b) => {
    const strA = String(a);
    const strB = String(b);
    if (/^\d{6}$/.test(strA) && /^\d{6}$/.test(strB)) {
      return parseInt(strA, 10) - parseInt(strB, 10);
    }
    const monthYearA = parseMonthYearSortKey(strA);
    const monthYearB = parseMonthYearSortKey(strB);
    if (monthYearA != null && monthYearB != null) {
      return monthYearA - monthYearB;
    }
    if (/^\d{4}-\d{2}$/.test(strA) && /^\d{4}-\d{2}$/.test(strB)) {
      return strA.localeCompare(strB);
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(strA) && /^\d{4}-\d{2}-\d{2}$/.test(strB)) {
      return strA.localeCompare(strB);
    }
    return strA.localeCompare(strB);
  });

  // Helper function to identify column types based on API type
  const getColumnType = (col, sampleValue, idx) => {
    const colType = col.type?.toUpperCase() || '';
    const colName = (col.name || '').toLowerCase();
    
    // First, check if it's a date/time type
    const isDateTimeType = colType.includes('TIMESTAMP') || 
                          colType.includes('DATE') || 
                          colType.includes('DATETIME') ||
                          colType.includes('TIME');
    
    if (isDateTimeType) {
      return 'time';
    }
    
    // Check API type to determine if numeric
    const isNumericType = colType.includes('INT') || 
                        colType.includes('DOUBLE') || 
                        colType.includes('FLOAT') || 
                        colType.includes('DECIMAL') ||
                        colType.includes('NUMERIC');
    
    // If API says it's numeric, return numeric (unless it's clearly a date identifier)
    if (isNumericType) {
      if (INDEX_COLUMN_PATTERN.test(colName)) {
        return 'numeric';
      }

      if (METADATA_DATE_COLUMN_PATTERN.test(colName)) {
        return 'categorical';
      }

      const isDateIdentifier = colName.includes('month') ||
                              colName.includes('date') ||
                              colName.includes('quarter') ||
                              colName.includes('year') ||
                              colName.includes('week') ||
                              colName.includes('day') ||
                              colName.includes('period');

      if (isDateIdentifier) {
        return 'time';
      }
      return 'numeric';
    }
    
    // Check if string/text type
    const isStringType = colType.includes('STRING') || 
                        colType.includes('TEXT') || 
                        colType.includes('VARCHAR') ||
                        colType.includes('CHAR');
    
    if (isStringType) {
      // Check if column name suggests it's a date/time column
      const isTimeColumnByName = colName.includes('month') || 
                                colName.includes('date') || 
                                colName.includes('quarter') || 
                                colName.includes('year') ||
                                colName.includes('week') ||
                                colName.includes('day') ||
                                colName.includes('period');
      
      if (INDEX_COLUMN_PATTERN.test(colName)) {
        return 'numeric';
      }

      if (METADATA_DATE_COLUMN_PATTERN.test(colName)) {
        return 'categorical';
      }

      const sampleStr = String(sampleValue ?? '').replace(/,/g, '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(sampleStr) ||
        /^\d{4}-\d{2}$/.test(sampleStr) ||
        /^[A-Za-z]{3}-\d{4}$/.test(sampleStr)) {
        return 'time';
      }

      if (isTimeColumnByName) {
        return 'time';
      }

      // Databricks preview rows often return numeric measures as strings
      if (sampleStr && !Number.isNaN(Number(sampleStr)) && !/^\d{6}$/.test(sampleStr)) {
        return 'numeric';
      }

      // Otherwise, it's categorical/text
      return 'categorical';
    }
    
    // Fallback: if no API type, try to infer from value
    const sampleStr = String(sampleValue || '');
    const isNumericValue = !isNaN(sampleValue) && 
                         sampleValue !== null && 
                         sampleValue !== '' 
                         //&&
                         //!sampleStr.match(/^\d{4}$/); // Exclude 4-digit years
    
    if (isNumericValue) {
      const isTimeColumnByName = colName.includes('month') ||
                                colName.includes('date') ||
                                colName.includes('quarter') ||
                                colName.includes('year') ||
                                colName.includes('week') ||
                                colName.includes('day') ||
                                colName.includes('period');
      if (isTimeColumnByName || /^\d{6}$/.test(sampleStr) || /^\d{4}-\d{2}$/.test(sampleStr)) {
        return 'time';
      }
      return 'numeric';
    }
    
    // Default to categorical
    return 'categorical';
  };

  const UNWANTED_TABLE_COLUMN_PATTERN = /maximum|max_?period|row_?num|rownum|sql_?debug|\blimit\b/i;

  const buildRowObjectsFromTableResult = (result) =>
    result.data.map(row => {
      const obj = {};
      result.columns.forEach((col, idx) => {
        obj[col.name] = row[idx];
      });
      return obj;
    });

  const isSummaryAggregateTable = (result) => {
    const title = result?.chartMetadata?.title || result?.tableMetadata?.title || '';
    if (chartTitleWantsTrendLine(title) || chartTitleWantsStackedGroupBar(title) || chartTitleWantsChangeMeasure(title)) {
      return false;
    }
    if (/\bsummary\b/i.test(title)) return true;

    if (!result?.columns || !result?.data || result.data.length === 0) return false;

    const columnTypes = result.columns.map((col, idx) => ({
      index: idx,
      name: col.name,
      type: getColumnType(col, result.data[0]?.[idx], idx),
    }));

    if (columnTypes.some(col => UNWANTED_TABLE_COLUMN_PATTERN.test(col.name || ''))) {
      return false;
    }

    const timeColumns = columnTypes.filter(col => col.type === 'time');
    const rowObjects = buildRowObjectsFromTableResult(result);
    const bestTimeColumn = selectBestTimeColumn(timeColumns, rowObjects);
    if (bestTimeColumn) {
      const uniqueTimes = new Set(result.data.map(row => String(row[bestTimeColumn.index])));
      if (uniqueTimes.size >= 3) return false;
    }

    return false;
  };

  const getResultDisplayTitle = (result) =>
    result?.chartMetadata?.title || result?.tableMetadata?.title || null;

  // Helper function to check if table should also show a line chart
  const shouldShowChartForTable = (result, allResults = []) => {
    if (!result.columns || !result.data || result.data.length === 0) return false;
    if (isSummaryAggregateTable(result)) return false;

    // Don't show chart if there's already a chart in the results
    if (hasChartInResults(allResults)) return false;
    
    // Need at least 2 columns
    if (result.columns.length < 2) return false;
    
    // Classify columns
    const columnTypes = result.columns.map((col, idx) => ({
      index: idx,
      name: col.name,
      type: getColumnType(col, result.data[0]?.[idx], idx)
    }));
    
    // Must have at least one numeric column (Y-axis)
    const numericColumns = columnTypes.filter(col => col.type === 'numeric');
    if (numericColumns.length === 0) return false;
    
    // Must have at least one non-numeric column (X-axis)
    const nonNumericColumns = columnTypes.filter(col => col.type !== 'numeric');
    if (nonNumericColumns.length === 0) return false;

    const timeColumns = columnTypes.filter(col => col.type === 'time');
    const rowObjects = buildRowObjectsFromTableResult(result);
    const bestTimeColumn = selectBestTimeColumn(timeColumns, rowObjects);
    if (!bestTimeColumn) return false;

    const uniqueTimes = new Set(result.data.map(row => String(row[bestTimeColumn.index])));
    if (uniqueTimes.size < 3) return false;
    
    return true;
  };

  // Helper function to render line chart from table data
  const renderLineChartFromTable = (result, chartId, originalResult = null, allResults = []) => {
    if (!result.data || result.data.length === 0) return null;

    // Check if this chart has custom axis settings from stored config
    const customConfig = chartConfigs[chartId] || null;

    // Process data to convert scientific notation for numeric columns
    const processedData = result.data.map(row => {
      return row.map((cell, idx) => {
        const col = result.columns[idx];
        const isNumeric = col.type && (
          col.type.toUpperCase().includes('INT') ||
          col.type.toUpperCase().includes('DOUBLE') ||
          col.type.toUpperCase().includes('FLOAT') ||
          col.type.toUpperCase().includes('DECIMAL') ||
          col.type.toUpperCase().includes('NUMERIC')
        );
        return convertScientificNotation(cell, isNumeric);
      });
    });

    // Classify all columns (use processed data for type detection)
    const columnTypes = result.columns.map((col, idx) => ({
      index: idx,
      name: col.name,
      type: getColumnType(col, processedData[0]?.[idx], idx)
    }));
    
    // Find time columns (prioritize for X-axis)
    const timeColumns = columnTypes.filter(col => col.type === 'time');
    
    // Find numeric columns (Y-axis candidates)
    const numericColumns = columnTypes.filter(col => col.type === 'numeric');
    
    if (numericColumns.length === 0) return null;
    
    // Get all non-numeric columns (for X-axis and legend)
    const nonNumericColumns = columnTypes.filter(col => col.type !== 'numeric');
    
    // Determine X-axis: use custom if set, otherwise prioritize time columns, then first non-numeric
    let xAxisColumn;
    let xAxisIndex;
    if (customConfig && customConfig.xAxis) {
      xAxisColumn = columnTypes.find(col => col.name === customConfig.xAxis && col.type !== 'numeric');
      if (xAxisColumn) {
        xAxisIndex = xAxisColumn.index;
      } else {
        // Fallback to default logic
        if (timeColumns.length > 0) {
          xAxisColumn = timeColumns[0];
          xAxisIndex = xAxisColumn.index;
        } else if (nonNumericColumns.length > 0) {
          xAxisColumn = nonNumericColumns[0];
          xAxisIndex = xAxisColumn.index;
        } else {
          return null;
        }
      }
    } else {
      if (timeColumns.length > 0) {
        xAxisColumn = timeColumns[0];
        xAxisIndex = xAxisColumn.index;
      } else if (nonNumericColumns.length > 0) {
        xAxisColumn = nonNumericColumns[0];
        xAxisIndex = xAxisColumn.index;
      } else {
        return null; // Need at least one non-numeric column for X-axis
      }
    }
    
    const xAxisKey = xAxisColumn.name;
    
    // Determine Y-axis: handle both single value and array (multiple Y-axis)
    let yAxisColumns = [];
    let yAxisIndices = [];
    let yAxisKeys = [];
    
    if (customConfig && customConfig.yAxis) {
      const yAxisConfig = customConfig.yAxis;
      // Check if it's an array (multiple Y-axis) or single value
      if (Array.isArray(yAxisConfig)) {
        yAxisConfig.forEach(yAxisName => {
          const col = numericColumns.find(c => c.name === yAxisName);
          if (col) {
            yAxisColumns.push(col);
            yAxisIndices.push(col.index);
            yAxisKeys.push(col.name);
          }
        });
      } else {
        // Single Y-axis value
        const col = numericColumns.find(c => c.name === yAxisConfig);
        if (col) {
          yAxisColumns.push(col);
          yAxisIndices.push(col.index);
          yAxisKeys.push(col.name);
        }
      }
    }
    
    // If no Y-axis found, use default (last numeric column)
    if (yAxisColumns.length === 0) {
      const yAxisColumn = numericColumns[numericColumns.length - 1];
      yAxisColumns = [yAxisColumn];
      yAxisIndices = [yAxisColumn.index];
      yAxisKeys = [yAxisColumn.name];
    }
    
    const hasMultipleYAxis = yAxisKeys.length > 1;
    const yAxisKey = yAxisKeys[0]; // For backward compatibility
    const yAxisIndex = yAxisIndices[0]; // For backward compatibility
    
    // Determine legend/grouping: 
    // - If multiple Y-axis, legend is 'none' (Y-axis values become the series)
    // - Otherwise, use custom if set, or find second non-numeric column
    let legendColumn;
    let legendIndex;
    if (hasMultipleYAxis || (customConfig && customConfig.legend === 'none')) {
      legendIndex = -1;
    } else if (customConfig && customConfig.legend !== undefined) {
      if (customConfig.legend === 'none' || customConfig.legend === '') {
        legendIndex = -1;
      } else {
        legendColumn = nonNumericColumns.find(col => col.name === customConfig.legend && col.index !== xAxisIndex);
        legendIndex = legendColumn ? legendColumn.index : -1;
      }
    } else {
      legendColumn = nonNumericColumns.find(col => col.index !== xAxisIndex);
      legendIndex = legendColumn ? legendColumn.index : -1;
    }
    const legendKey = legendColumn ? legendColumn.name : null;

    // Determine chart type: use custom config if available, otherwise default based on time column
    const hasTimeColumn = timeColumns.length > 0;
    const defaultChartType = hasTimeColumn ? 'line' : 'bar';
    const chartType = customConfig?.chartType || defaultChartType;

    let chartData;
    let seriesKeys = [];

    // If multiple Y-axis, use them as series
    if (hasMultipleYAxis) {
      const uniqueXValues = [...new Set(processedData.map(row => String(row[xAxisIndex])))].sort();
      
      chartData = uniqueXValues.map(xValue => {
        const dataPoint = { [xAxisKey]: xValue };
        
        // For each Y-axis column, get the value for this X value
        yAxisKeys.forEach((yKey, idx) => {
          const yIdx = yAxisIndices[idx];
          const matchingRows = processedData.filter(r => String(r[xAxisIndex]) === String(xValue));
          
          if (matchingRows.length > 0) {
            // Sum the Y-axis values for this X value
            const sum = matchingRows.reduce((acc, row) => {
              const yValue = row[yIdx];
              const numValue = isNaN(yValue) ? 0 : Number(yValue);
              return acc + numValue;
            }, 0);
            dataPoint[yKey] = sum;
          } else {
            dataPoint[yKey] = 0;
          }
        });
        
        return dataPoint;
      });
      
      seriesKeys = yAxisKeys;
    } else if (legendIndex !== -1) {
      // Case: Has grouping column - group by X-axis and legend, aggregate Y-axis
      const uniqueGroups = [...new Set(processedData.map(row => String(row[legendIndex])))];
      seriesKeys = uniqueGroups;
      
      // Get unique X-axis values
      const uniqueXValues = [...new Set(processedData.map(row => String(row[xAxisIndex])))].sort();
      
      // Transform data: group by X-axis and legend, sum Y-axis values
      chartData = uniqueXValues.map(xValue => {
        const dataPoint = { [xAxisKey]: xValue };
        
        // For each grouping value, aggregate Y values
        uniqueGroups.forEach(groupValue => {
          // Find all rows matching this X and legend combination
          const matchingRows = processedData.filter(r => 
            String(r[xAxisIndex]) === String(xValue) && String(r[legendIndex]) === String(groupValue)
          );
          
          if (matchingRows.length > 0) {
            // Sum the Y-axis values
            const sum = matchingRows.reduce((acc, row) => {
              const yValue = row[yAxisIndex];
              const numValue = isNaN(yValue) ? 0 : Number(yValue);
              return acc + numValue;
            }, 0);
            dataPoint[groupValue] = sum;
          } else {
            dataPoint[groupValue] = null;
          }
        });
        
        return dataPoint;
      });
    } else {
      // Case: No grouping column - group by X-axis, aggregate Y-axis
      const uniqueXValues = [...new Set(processedData.map(row => String(row[xAxisIndex])))].sort();
      
      chartData = uniqueXValues.map(xValue => {
        const dataPoint = { [xAxisKey]: xValue };
        
        // Find all rows matching this X value
        const matchingRows = processedData.filter(r => String(r[xAxisIndex]) === String(xValue));
        
        // Sum the Y-axis values for this X value
        const sum = matchingRows.reduce((acc, row) => {
          const yValue = row[yAxisIndex];
          const numValue = isNaN(yValue) ? 0 : Number(yValue);
          return acc + numValue;
        }, 0);
        
        dataPoint[yAxisKey] = sum;
        
        return dataPoint;
      });
      
      seriesKeys = [yAxisKey];
    }

    const colors = ['#005587', '#00BFB3', '#43B02A', '#00A3E0', '#008F86', '#027223', '#FE8A12']; // IQVIA Brand Colors
    const xAxisLabel = xAxisKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const yAxisLabel = yAxisKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const finalChartId = `table-chart-${chartId}`;

    // Create a result-like object for download functions (use processed data)
    const chartResult = {
      ...result,
      data: processedData,
      chartMetadata: { chartType: chartType }
    };

    return (
      <Box sx={{ mt: 3 }}>
        <Box className="result-header" sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ChartIcon sx={{ fontSize: 20, color: '#00A3E0', mr: 1.5 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#005587', fontSize: '14px' }}>
              {chartType === 'bar' ? 'Bar Chart' : 
               chartType === 'line' ? 'Line Chart' :
               chartType === 'area' ? 'Area Chart' :
               chartType === 'scatter' ? 'Scatter Chart' :
               chartType === 'pie' ? 'Pie Chart' :
               chartType === 'radialbar' ? 'Radial Bar Chart' : 'Chart'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              // Prepare available columns for selection
              const availableColumns = result.columns.map((col, idx) => ({
                name: col.name,
                type: getColumnType(col, result.data[0]?.[idx], idx),
                index: idx
              }));
              
              // Get saved configuration if exists
              const savedConfig = chartConfigs[chartId] || null;
              
              // For pie/radialbar: legend = xAxis (labels), value = yAxis (data)
              // For others: xAxis, yAxis, legend as normal
              const isPieOrRadialBar = chartType === 'pie' || chartType === 'radialbar';
              
              // Use saved config values if available, otherwise use calculated defaults
              const savedChartType = savedConfig?.chartType || chartType;
              const isPieOrRadialBarSaved = savedChartType === 'pie' || savedChartType === 'radialbar';
              
              setChartEditDialog({
                open: true,
                chartId: chartId,
                chartType: 'table',
                originalData: result,
                xAxis: isPieOrRadialBarSaved ? '' : (savedConfig?.xAxis || xAxisKey),
                yAxis: isPieOrRadialBarSaved ? [] : (
                  savedConfig?.yAxis 
                    ? (Array.isArray(savedConfig.yAxis) ? savedConfig.yAxis : [savedConfig.yAxis])
                    : (yAxisKey ? [yAxisKey] : [])
                ),
                legend: isPieOrRadialBarSaved 
                  ? (savedConfig?.legend || xAxisKey)
                  : (savedConfig?.legend && savedConfig.legend !== 'none' ? savedConfig.legend : (legendKey || '')),
                value: isPieOrRadialBarSaved ? (savedConfig?.value || yAxisKey) : '',
                selectedChartType: savedChartType,
                availableColumns: availableColumns,
              });
            }}
            sx={{
              backgroundColor: '#00A3E0',
              color: '#fff',
              padding: '6px',
              '&:hover': {
                backgroundColor: '#005587',
              },
            }}
            title="Edit Chart"
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Paper className="chart-result-container" sx={{ p: 3 }}>
          <Box>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 0.5,
              mb: 1,
            }}>
              <IconButton
                size="small"
                onClick={() => downloadChartAsExcel(chartResult, `SiMS_Data_Exploration_${chartType}_chart_${chartId}`)}
                sx={{
                  backgroundColor: '#00BFB3',
                  color: '#fff',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: '#008577',
                  },
                }}
                title="Download as Excel"
              >
                <ExcelIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={async () => await downloadChartAsPDF(chartResult, `SiMS_Data_Exploration_${chartType}_chart_${chartId}`, chartId, getLastBotDescription())}
                sx={{
                  backgroundColor: '#d32f2f',
                  color: '#fff',
                  padding: '6px',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  },
                }}
                title="Download as PDF"
              >
                <PdfIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Box id={chartId}>
              {chartType === 'pie' || chartType === 'radialbar' ? (() => {
                const pieValueKey = customConfig?.value || yAxisKey;
                const pieLabelKey = customConfig?.legend || xAxisKey;
                
                // Convert processedData to objects
                const rawPieData = processedData.map(row => {
                  const rowObj = {};
                  result.columns.forEach((col, idx) => {
                    rowObj[col.name] = row[idx];
                  });
                  return rowObj;
                });
                
                // Aggregate data by legend column (pieLabelKey) for pie charts
                const aggregatedPieDataMap = new Map();
                rawPieData.forEach(item => {
                  const label = item[pieLabelKey];
                  const value = item[pieValueKey];
                  
                  if (label !== null && label !== undefined) {
                    const labelStr = String(label);
                    const numValue = typeof value === 'number' ? value : parseFloat(value);
                    const validValue = isNaN(numValue) ? 0 : numValue;
                    
                    if (aggregatedPieDataMap.has(labelStr)) {
                      aggregatedPieDataMap.set(labelStr, aggregatedPieDataMap.get(labelStr) + validValue);
                    } else {
                      aggregatedPieDataMap.set(labelStr, validValue);
                    }
                  }
                });
                
                // Convert map back to array format
                const aggregatedPieData = Array.from(aggregatedPieDataMap.entries()).map(([name, value]) => ({
                  [pieLabelKey]: name,
                  [pieValueKey]: value
                }));
                
                return (
                  <ChartRenderer
                    chartType={chartType}
                    chartData={aggregatedPieData}
                    pieDataKey={pieValueKey}
                    pieNameKey={pieLabelKey}
                    chartId={chartId}
                  />
                );
              })() : (
                <ChartRenderer
                  chartType={chartType}
                  chartData={chartData}
                  xAxisKey={xAxisKey}
                  seriesKeys={seriesKeys}
                  xAxisLabel={xAxisLabel}
                  yAxisLabel={yAxisLabel}
                  colors={colors}
                  chartId={chartId}
                  xAxisLabelStyle={{ fontSize: 13, fontWeight: 600 }}
                  yAxisLabelStyle={{ fontSize: 13, fontWeight: 600 }}
                  showLegend={seriesKeys.length > 1}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  // Handle chart edit dialog close
  const handleChartEditClose = useCallback(() => {
    setChartEditDialog(prev => ({ ...prev, open: false }));
  }, []);

  // Handle chart edit apply
  const handleChartEditApply = useCallback(() => {
    const { chartId, xAxis, yAxis, legend, value, selectedChartType } = chartEditDialog;
    
    // Normalize yAxis to array
    const yAxisArray = Array.isArray(yAxis) ? yAxis : (yAxis ? [yAxis] : []);
    const hasMultipleYAxis = yAxisArray.length > 1;
    const finalLegend = hasMultipleYAxis ? 'none' : (legend || 'none');
    
    // Validation based on chart type
    const isPieOrRadialBar = selectedChartType === 'pie' || selectedChartType === 'radialbar';
    
    if (isPieOrRadialBar) {
      // For pie/radialbar: need legend (for labels) and value (for data)
      if (!legend || !value) {
        alert('Please select both Legend (for labels) and Value (for data)');
        return;
      }
    } else {
      // For other charts: need x-axis and at least one y-axis
      if (!xAxis || yAxisArray.length === 0) {
        alert('Please select X-axis and at least one Y-axis');
        return;
      }
    }
    
    // Store the configuration
    // If multiple Y-axis, store as array; otherwise store as single value for backward compatibility
    setChartConfigs(prev => ({
      ...prev,
      [chartId]: { 
        xAxis, 
        yAxis: hasMultipleYAxis ? yAxisArray : yAxisArray[0], 
        legend: finalLegend, 
        value: value || '',
        chartType: selectedChartType 
      }
    }));
    
    // Close dialog
    handleChartEditClose();
  }, [chartEditDialog, handleChartEditClose]);

  // Memoized render functions to prevent recreation on each render
  const renderResult = useCallback((result, index, allResults = [], messageId = null) => {
    // Create unique key for pagination: messageId + index
    const uniqueKey = messageId ? `${messageId}-${index}` : `result-${index}`;
    
    const displayAsTable = result.visualizationType === 'table' ||
      (result.visualizationType === 'chart' && isSummaryAggregateTable(result));
    const resultTitle = getResultDisplayTitle(result);

    return (
      <Box key={index} sx={{ mt: 2 }}>
        {displayAsTable && (
          <Box>
            <Box className="result-header">
              <TableIcon sx={{ fontSize: 20, color: '#00A3E0', mr: 1.5 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#005587', fontSize: '14px' }}>
                {resultTitle || `Table Results (${result.totalRows ?? result.data?.length ?? 0} rows)`}
              </Typography>
            </Box>
            {renderTable(result, uniqueKey)}
            {/* Show line chart if conditions are met */}
            {shouldShowChartForTable(result, allResults) && renderLineChartFromTable(result, uniqueKey, result, allResults)}
            {/* {result.query && (
              <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(0, 125, 195, 0.03)', borderRadius: '8px', border: '1px solid rgba(0, 125, 195, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#005587', fontStyle: 'italic', fontWeight: 500, fontSize: '11px' }}>
                  📊 Query: {result.query}
                </Typography>
              </Box>
            )} */}
          </Box>
        )}
                {result.visualizationType === 'chart' && !displayAsTable && (
          <Box>
            <Box className="result-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChartIcon sx={{ fontSize: 20, color: '#00A3E0', mr: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#005587', fontSize: '14px' }}>
                  {(() => {
                    const title = result?.chartMetadata?.title;
                    if (title && title.length > 0) return title;
                    const ct = result?.chartMetadata?.chartType;
                    return (ct ? (ct.toString().toUpperCase() + ' Chart') : 'Chart');
                  })()}
                </Typography>
              </Box>
              {(result.chartMetadata?.chartType === 'line' || result.chartMetadata?.chartType === 'bar' || !result.chartMetadata?.chartType) && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const uniqueChartId = messageId ? `chart-${messageId}-${index}` : `chart-${index}`;
                    // Prepare available columns for selection
                    const availableColumns = result.columns.map((col, idx) => ({
                      name: col.name,
                      type: getColumnType(col, result.data[0]?.[idx], idx),
                      index: idx
                    }));
                    
                    // Determine current axes (will be set in renderChart, but we need defaults here)
                    const nonNumericColumns = availableColumns.filter(col => col.type !== 'numeric');
                    const numericColumns = availableColumns.filter(col => col.type === 'numeric');
                    const currentXAxis = nonNumericColumns.length > 0 ? nonNumericColumns[0].name : result.columns[0]?.name;
                    const currentYAxis = numericColumns.length > 0 ? numericColumns[numericColumns.length - 1].name : result.columns[result.columns.length - 1]?.name;
                    
                    setChartEditDialog({
                      open: true,
                      chartId: uniqueChartId,
                      chartType: 'api-chart',
                      originalData: result,
                      xAxis: currentXAxis,
                      yAxis: currentYAxis,
                      legend: '',
                      availableColumns: availableColumns,
                    });
                  }}
                  sx={{
                    backgroundColor: '#00A3E0',
                    color: '#fff',
                    padding: '6px',
                    '&:hover': {
                      backgroundColor: '#005587',
                    },
                  }}
                  title="Edit Chart"
                >
                  <EditIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
            <Paper className="chart-result-container" sx={{ p: 3 }}>
              {renderChart(result, index, messageId)}
            </Paper>
            {/* {result.query && (
              <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(0, 125, 195, 0.03)', borderRadius: '8px', border: '1px solid rgba(0, 125, 195, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#005587', fontStyle: 'italic', fontWeight: 500, fontSize: '11px' }}>
                  📊 Query: {result.query}
                </Typography>
              </Box>
            )} */}
          </Box>
        )}
      </Box>
    );
  }, [renderTable, renderChart, shouldShowChartForTable, renderLineChartFromTable, chartConfigs, getColumnType]);

  // Count user messages in current conversation - memoized to prevent recalculation
  const userMessageCount = useMemo(() => 
    messages.filter(msg => msg.sender === 'user').length, 
    [messages]
  );
  const isConversationLimitReached = useMemo(() => 
    userMessageCount >= MAX_CONVERSATION_MESSAGES, 
    [userMessageCount]
  );

  // Memoized send message handler - called from InputArea component
  const handleSendMessage = useCallback(async (questionText, queryType = '') => {
    if (!questionText || !questionText.trim()) return;

    // Check conversation message limit
    if (userMessageCount >= MAX_CONVERSATION_MESSAGES) {
      dispatch(addErrorMessage({ 
        text: `Conversation limit reached. You can send up to ${MAX_CONVERSATION_MESSAGES} messages per conversation. Please start a new chat.` 
      }));
      return;
    }

    // Check input character limit
    if (questionText.length > MAX_INPUT_CHARACTERS) {
      dispatch(addErrorMessage({ 
        text: `Message is too long. Maximum ${MAX_INPUT_CHARACTERS} characters allowed.` 
      }));
      return;
    }
    
    // Add user message to Redux store
    dispatch(addUserMessage({ text: questionText }));
    
    try {
      // If this is the first message or no conversation ID exists, start a new conversation
      if (!currentConversationId) {
        const resultAction = await dispatch(startConversation({ 
          question: questionText,
          queryType: queryType 
        }));
        
        if (startConversation.rejected.match(resultAction)) {
          // Handle error
          dispatch(addErrorMessage({ 
            text: `Sorry, I encountered an error: ${resultAction.payload || 'Unknown error'}` 
          }));
        }
      } else {
        // Continue existing conversation
        const resultAction = await dispatch(continueChat({ 
          conversationId: currentConversationId, 
          question: questionText,
          chatId: currentDatabaseChatId, // Send ChatID if user is in the same chat
          queryType: queryType,
          isGenieConversation: isGenieConversation // Pass flag to API to know if this is a Genie conversation
        }));
        
        if (continueChat.rejected.match(resultAction)) {
          // Handle error
          dispatch(addErrorMessage({ 
            text: `Sorry, I encountered an error: ${resultAction.payload || 'Unknown error'}` 
          }));
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      dispatch(addErrorMessage({ 
        text: `Sorry, I encountered an error: ${err.message || 'Unknown error'}` 
      }));
    }
  }, [dispatch, currentConversationId, userMessageCount, currentDatabaseChatId]);

  const handleNewChat = useCallback(() => {
    // Check if there's already an empty "New Chat" (one without conversationId)
    const existingEmptyChat = chatHistory.find(
      chat => chat.title === 'New Chat' && !chat.conversationId
    );
    
    if (existingEmptyChat) {
      // Reuse the existing empty chat
      dispatch(setCurrentChat({ 
        chatId: existingEmptyChat.id, 
        conversationId: null 
      }));
      dispatch(clearMessages());
      dispatch(clearError());
      dispatch(clearFollowupQuestions());
    } else {
      // Create a new chat only if no empty chat exists
      const newChatData = {
        id: `chat-${Date.now()}`,
        title: 'New Chat',
        conversationId: null,
      };
      dispatch(addNewChat(newChatData));
      dispatch(clearMessages());
      dispatch(clearError());
    }
  }, [chatHistory, dispatch]);

  const handleSelectChat = useCallback(async (chat) => {
    // Clear messages first
    dispatch(clearMessages());
    dispatch(clearError());
    dispatch(clearFollowupQuestions());
    
    // Set the current chat
    dispatch(setCurrentChat({ 
      chatId: chat.id, 
      conversationId: chat.conversationId,
      databaseChatId: chat.chatId || null // Pass database ChatID from history
    }));
    
    // If this chat has a chatId (database ChatID), load the conversation history
    if (chat.chatId) {
      try {
        const resultAction = await dispatch(getConversationHistory({ chatId: chat.chatId }));
        if (getConversationHistory.rejected.match(resultAction)) {
          dispatch(addErrorMessage({ 
            text: `Failed to load conversation: ${resultAction.payload || 'Unknown error'}` 
          }));
        }
      } catch (err) {
        console.error('Error loading conversation history:', err);
        dispatch(addErrorMessage({ 
          text: `Failed to load conversation: ${err.message || 'Unknown error'}` 
        }));
      }
    }
  }, [dispatch]);

  const handleDeleteChat = useCallback((chatId) => {
    dispatch(deleteChat({ chatId }));
    if (chatId === currentChatId) {
      const remainingChats = chatHistory.filter((chat) => chat.id !== chatId);
      if (remainingChats.length > 0) {
        handleSelectChat(remainingChats[0]);
      } else {
        handleNewChat();
      }
    }
  }, [dispatch, currentChatId, chatHistory, handleSelectChat, handleNewChat]);

  const toggleDrawer = useCallback(() => {
    setDrawerOpen(prev => !prev);
  }, []);

  // Memoized sorted chat history to prevent re-sorting on every render
  const sortedChatHistory = useMemo(() => {
    return chatHistory
      .filter(chat => chat.conversationId !== null)
      .sort((a, b) => {
        const timeA = new Date(a.timestamp || 0).getTime();
        const timeB = new Date(b.timestamp || 0).getTime();
        return timeB - timeA;
      });
  }, [chatHistory]);

  // Memoized conversation count
  const conversationCount = useMemo(() => sortedChatHistory.length, [sortedChatHistory]);

  return (
    <Box className="chatbot-container">
      {/* Sidebar/Drawer */}
      <Box
        className="chat-sidebar"
        sx={{
          width: drawerOpen ? DRAWER_WIDTH : 0,
          minWidth: drawerOpen ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          backgroundColor: '#F8F8F8',
          color: '#303030',
          borderRight: drawerOpen ? '1px solid #e0e0e0' : 'none',
          height: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease-in-out, min-width 0.3s ease-in-out',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c0c0c0',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a0a0a0',
          },
        }}
      >
        <Box className="sidebar-header" sx={{ minWidth: DRAWER_WIDTH }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#005587',
                fontWeight: 700,
                fontSize: '14px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
              }}
            >
              Chat History
            </Typography>
            <IconButton
              onClick={toggleDrawer}
              sx={{
                color: '#808080',
                padding: '8px',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                  color: '#303030',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>

        <Box className="chat-history-section" sx={{ minWidth: DRAWER_WIDTH }}>
          <List sx={{ padding: '8px' }}>
            {sortedChatHistory.map((chat) => (
              <ChatHistoryItem
                key={chat.id}
                chat={chat}
                isSelected={chat.id === currentChatId}
                onSelect={handleSelectChat}
              />
            ))}
          </List>
        </Box>

        <Box sx={{ padding: '16px', marginTop: 'auto', borderTop: '1px solid #e0e0e0' }}>
          <Typography variant="caption" sx={{ color: '#808080', fontSize: '11px', whiteSpace: 'nowrap', fontWeight: 500 }}>
            {conversationCount} conversation{conversationCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Main Chat Area */}
      <Box
        className="chat-main-area"
        sx={{
          flex: 1,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {/* Header */}
        <Box className="chat-header" sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 16px',
              gap: 2,
            }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
            <IconButton
              onClick={toggleDrawer}
              size="large"
              sx={{
                color: '#005587',
                backgroundColor: 'rgba(0, 125, 195, 0.05)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 125, 195, 0.15)',
                },
                padding: '10px',
              }}
            >
              <MenuIcon sx={{ fontSize: 28 }} />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewChat}
              sx={{
                backgroundColor: '#00A3E0',
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                padding: '8px 20px',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                boxShadow: '0 2px 4px rgba(0, 125, 195, 0.2)',
                '&:hover': {
                  backgroundColor: '#005587',
                  boxShadow: '0 4px 8px rgba(0, 125, 195, 0.3)',
                },
              }}
            >
              New Chat
            </Button>
          </Box>
          {messages.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <FormControl size="small" sx={{ minWidth: 130, maxWidth: 160 }}>
                <InputLabel id="insights-country-select-label" sx={{ fontSize: '13px' }}>Country</InputLabel>
                <Select
                  labelId="insights-country-select-label"
                  id="insights-country-select"
                  value={countryOptions.includes(selectedCountry) ? selectedCountry : (countryOptions[0] || '')}
                  label="Country"
                  onChange={(e) => handleCountryChange(e.target.value)}
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '13px',
                    height: 36,
                    '& .MuiSelect-select': { py: 0.75 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00A3E0' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#005587' },
                  }}
                >
                  {countryOptions.map((country) => (
                    <MenuItem key={country} value={country}>{country}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120, maxWidth: 150 }}>
                <InputLabel id="insights-product-select-label" sx={{ fontSize: '13px' }}>Product</InputLabel>
                <Select
                  labelId="insights-product-select-label"
                  id="insights-product-select"
                  value={brandOptions.includes(selectedBrand) ? selectedBrand : (brandOptions[0] || '')}
                  label="Product"
                  onChange={(e) => handleBrandChange(e.target.value)}
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '13px',
                    height: 36,
                    '& .MuiSelect-select': { py: 0.75 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00A3E0' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#005587' },
                  }}
                >
                  {brandOptions.map((brand) => (
                    <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220, maxWidth: 320 }}>
                <InputLabel id="insights-sku-select-label" sx={{ fontSize: '13px' }}>SKU</InputLabel>
                <Select
                  labelId="insights-sku-select-label"
                  id="insights-sku-select"
                  value={skuOptions.includes(selectedSku) ? selectedSku : (skuOptions[0] || '')}
                  label="SKU"
                  onChange={(e) => setSelectedSku(e.target.value)}
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '12px',
                    height: 36,
                    '& .MuiSelect-select': { py: 0.75 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00A3E0' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#005587' },
                  }}
                >
                  {skuOptions.map((sku) => (
                    <MenuItem key={sku} value={sku} sx={{ fontSize: '12px' }}>{sku}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Constrained Factor"
                value={constrainedFactor}
                onChange={(e) => {
                  const next = e.target.value;
                  if (isValidConstrainedFactorInput(next)) {
                    setConstrainedFactor(next);
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    e.key.length === 1 &&
                    !/[0-9.\-]/.test(e.key) &&
                    !e.ctrlKey &&
                    !e.metaKey &&
                    !e.altKey
                  ) {
                    e.preventDefault();
                  }
                }}
                inputProps={{
                  inputMode: 'decimal',
                  pattern: '-?[0-9]*\\.?[0-9]*',
                  'aria-label': 'Constrained Factor percentage',
                }}
                placeholder={String(DEFAULT_CONSTRAINED_FACTOR)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                sx={{
                  width: 140,
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-root': {
                    fontWeight: 600,
                    fontSize: '14px',
                    borderRadius: '8px',
                    height: 36,
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00A3E0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#005587' },
                }}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: 1, minWidth: 0 }}>
            {messages.length === 0 && (
              <Button
                variant="contained"
                onClick={handleOpenSupplyRiskHeatMap}
                sx={{
                  backgroundColor: '#00A3E0',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  padding: '8px 20px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  boxShadow: '0 2px 4px rgba(0, 125, 195, 0.2)',
                  '&:hover': {
                    backgroundColor: '#005587',
                    boxShadow: '0 4px 8px rgba(0, 125, 195, 0.3)',
                  },
                }}
              >
                Supply Risk Heat Map
              </Button>
            )}
          </Box>
        </Box>

        {/* Error Alert */}
        {isError && errorMessage && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error" onClose={() => dispatch(clearError())}>
              {errorMessage}
            </Alert>
          </Box>
        )}

        {/* Messages Area */}
        <Box className="messages-container" sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
          {isLoading && messages.length === 0 ? (
            <Box className="messages-list" sx={{ width: '100%' }}>
              {/* Skeleton Loader for Conversation History */}
              {[1, 2, 3, 4].map((index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: index % 2 === 0 ? 'flex-end' : 'flex-start',
                    mb: 3,
                    alignItems: 'flex-start',
                    width: '100%',
                  }}
                >
                  {index % 2 !== 0 && (
                    <Avatar
                      sx={{
                        bgcolor: '#e0e0e0',
                        width: 36,
                        height: 36,
                        mr: 2,
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <BotIcon sx={{ fontSize: 20, color: '#bdbdbd' }} />
                    </Avatar>
                  )}
                  <Box sx={{ maxWidth: index % 2 === 0 ? '70%' : '85%', width: '100%', flex: '0 1 auto' }}>
                    <Paper
                      sx={{
                        padding: '16px',
                        backgroundColor: index % 2 === 0 ? '#e3f2fd' : '#F8F8F8',
                        borderRadius: index % 2 === 0 ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      }}
                    >
                      <Skeleton variant="text" width={index % 2 === 0 ? "70%" : "60%"} height={24} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width={index % 2 === 0 ? "85%" : "80%"} height={20} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width={index % 2 === 0 ? "50%" : "45%"} height={20} />
                      {index % 2 !== 0 && index === 3 && (
                        <Box sx={{ mt: 2 }}>
                          <Skeleton variant="rectangular" width="100%" height={150} sx={{ borderRadius: 1 }} />
                        </Box>
                      )}
                    </Paper>
                  </Box>
                  {index % 2 === 0 && (
                    <Avatar
                      sx={{
                        bgcolor: '#e0e0e0',
                        width: 36,
                        height: 36,
                        ml: 2,
                        mt: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 20, color: '#bdbdbd' }} />
                    </Avatar>
                  )}
                </Box>
              ))}
            </Box>
          ) : messages.length === 0 ? (
            <Box className="welcome-screen">
              {/* KPI Cards Container - fills available space */}
              <Box 
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '0 0 auto',
                  minHeight: 'auto',
                  width: '100%',
                  gap: { xs: 0.5, sm: 0.75, md: 1 },
                }}
              >
              {/* KPI Summary Row */}
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 0.5, sm: 0.75, md: 1 },
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                  width: '100%',
                  px: { xs: 0.5, sm: 1, md: 1 },
                  flexShrink: 0,
                }}
              >
                {displayKpiCards.map((card) => (
                  <MiniKPICard
                    key={card.key}
                    title={card.title}
                    value={card.value}
                    subtitle={card.subtitle}
                    color={card.color}
                    isDemoData={DEMO_DATA_KPI_KEYS.has(card.key)}
                  />
                ))}
              </Box>

              {/* KPI Dashboard Cards - Row 2: forecast (left) + top markets (right) */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: { xs: 0.5, sm: 0.75, md: 1 }, 
                  flex: '0 0 auto',
                  minHeight: 'auto',
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                  justifyContent: 'center',
                  maxWidth: '100%',
                  width: '100%',
                  px: { xs: 0.5, sm: 1, md: 1 },
                }}
              >
                {FEATURE_FLAGS.SHOW_FORECAST_ON_WELCOME && (
                  <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '420px' }, display: 'flex' }}>
                    <UnconstrainedForecastChart
                      key={`${selectedCountry}-${selectedBrand}-${selectedSku}-${constrainedFactor}`}
                      data={productScopedDashboard.forecastData}
                      productName={selectedBrand}
                    />
                  </Box>
                )}
                <MiniChartCard
                  title={KPI_CARD_CONFIG.topCountriesHighestDemand.title}
                  chartType={KPI_CARD_CONFIG.topCountriesHighestDemand.chartType}
                  data={dashboardCards?.topCountriesHighestDemand?.data || []}
                  isLoading={isLoadingDashboardCards}
                  nameKey="name"
                  valueKey="value"
                  unit="(Units)"
                  iconType="emergency"
                  showPercentage={false}
                  stretchToRow={true}
                  isDemoData
                />
              </Box>

              {/* KPI Dashboard Cards - Row 3 */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: { xs: 0.5, sm: 0.75, md: 1 }, 
                  flex: '0 0 auto',
                  minHeight: 'auto',
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                  alignItems: { xs: 'stretch', md: 'stretch' },
                  justifyContent: 'center',
                  maxWidth: '100%',
                  width: '100%',
                  px: { xs: 0.5, sm: 1, md: 1 },
                }}
              >
                {FIRST_ROW_KPI_CARDS.map((rowCard) => {
                  const cardConfig = KPI_CARD_CONFIG[rowCard.id];
                  if (!cardConfig) return null;

                  let chartData = dashboardCards?.[rowCard.id]?.data || [];
                  let chartTitle = cardConfig.title;

                  if (rowCard.id === 'adherenceToProductObjectives') {
                    chartData = productScopedDashboard.signalsData;
                  } else if (rowCard.id === 'quotasPendingApproval') {
                    chartData = productScopedDashboard.buildUpData;
                    chartTitle = productScopedDashboard.buildUpTitle;
                  }

                  return (
                    <MiniChartCard
                      key={`${rowCard.id}-${selectedBrand}`}
                      title={chartTitle}
                      chartType={cardConfig.chartType}
                      data={chartData}
                      isLoading={isLoadingDashboardCards}
                      nameKey={cardConfig.nameKey}
                      valueKey={cardConfig.valueKey}
                      unit={rowCard.unit || cardConfig.unit || ''}
                      iconType={cardConfig.iconType}
                      showPercentage={Boolean(rowCard.showPercentage)}
                      minHeightOverride={rowCard.minHeightOverride}
                      isDemoData={Boolean(rowCard.isDemoData)}
                    />
                  );
                })}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  px: { xs: 0.5, sm: 1, md: 1 },
                  color: '#80868b',
                  fontSize: '11px',
                  lineHeight: 1.4,
                  fontStyle: 'italic',
                }}
              >
                {DEMO_DATA_FOOTNOTE}
              </Typography>

              {/* Alerts Row - shown below the second chart row */}
              {FEATURE_FLAGS.DISPLAY_DRIVER_ALERTS && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: { xs: 0.75, sm: 1, md: 1 },
                    width: '100%',
                    px: { xs: 0.5, sm: 1, md: 1 },
                    pb: { xs: 0.5, sm: 0.75, md: 1 },
                    flexShrink: 0,
                  }}
                >
                  {alertCards.map((alertCard) => {
                    const hasAlertDetails = Array.isArray(alertCard.details) && alertCard.details.length > 0;
                    const alertCount = Array.isArray(alertCard.details) ? alertCard.details.length : 0;
                    const subtitleText = alertCount === 0
                      ? 'No Signals Detected'
                      : `${alertCount} New Signal${alertCount > 1 ? 's' : ''} Detected`;

                    return (
                    <Paper
                      key={alertCard.key}
                      elevation={0}
                      sx={{
                        borderRadius: '10px',
                        border: hasAlertDetails ? '1px solid #FAD7B2' : '1px solid #BFE7C8',
                        background: hasAlertDetails
                          ? 'linear-gradient(180deg, #FFF8EE 0%, #FFF2E2 100%)'
                          : 'linear-gradient(180deg, #F4FFF7 0%, #EAF9EF 100%)',
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: '68px',
                      }}
                    >
                      <Box sx={{ minWidth: 0, mr: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: hasAlertDetails ? '#E46A00' : '#1F7A3A',
                            fontSize: '16px',
                            lineHeight: 1.2,
                          }}
                        >
                          {alertCard.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.35,
                            color: '#6F6F6F',
                            fontSize: '13px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {subtitleText}
                        </Typography>
                      </Box>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => handleOpenAlertDialog(alertCard)}
                        sx={{
                          minWidth: 'auto',
                          px: 1,
                          color: '#4F4F4F',
                          fontWeight: 600,
                          textTransform: 'none',
                          flexShrink: 0,
                        }}
                      >
                        More
                      </Button>
                    </Paper>
                  );
                  })}
                </Box>
              )}
              </Box>

              {/* Welcome Message and FAQs Section - Below KPI cards */}
              {FEATURE_FLAGS.SHOW_FAQS_ON_WELCOME && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'flex-start',
                    py: { xs: 0.5, sm: 0.75, md: 1 },
                    flexShrink: 0,
                    width: '100%',
                    gap: { xs: 0.5, sm: 0.75, md: 1 },
                  }}
                >
                  <Typography 
                    variant="h1" 
                    sx={{ 
                      fontWeight: 600, 
                      color: '#202124', 
                      fontSize: { xs: '14px', sm: '16px', md: '18px', lg: '20px' }, 
                      letterSpacing: '-0.3px',
                      textAlign: 'center',
                    }}
                  >
                    How can I help you today?
                  </Typography>
                  

                {/* FAQ Cards Section */}
                {mostAskedQuestions && mostAskedQuestions.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: { xs: 0.5, sm: 0.75, md: 1 },
                      justifyContent: 'center',
                      width: '100%',
                      maxWidth: '100%',
                      px: { xs: 0.5, sm: 1, md: 1 },
                    }}
                  >
                    {mostAskedQuestions.slice(0, 4).map((question, index) => (
                      <Box
                        key={index}
                        sx={{
                          flex: { xs: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 8px)' },
                          minWidth: { xs: 'calc(50% - 8px)', sm: 'calc(50% - 8px)', md: 'calc(25% - 8px)' },
                          maxWidth: { xs: 'calc(50% - 8px)', sm: 'calc(50% - 8px)', md: 'calc(25% - 8px)' },
                        }}
                      >
                        <QuestionPromptCard
                          prompt={question}
                          onInputChange={(text) => {
                            if (window.__chatbotSetInput) {
                              window.__chatbotSetInput(text);
                            }
                          }}
                          padding="8px 12px"
                          queryType={queryType}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Loading skeleton for FAQs */}
                {isLoadingMostAskedQuestions && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: { xs: 0.5, sm: 0.75, md: 1 },
                      justifyContent: 'center',
                      width: '100%',
                      maxWidth: '100%',
                      px: { xs: 0.5, sm: 1, md: 1 },
                    }}
                  >
                    {[1, 2, 3, 4].map((_, index) => (
                      <Box
                        key={index}
                        sx={{
                          flex: { xs: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 8px)' },
                          minWidth: { xs: 'calc(50% - 8px)', sm: 'calc(50% - 8px)', md: 'calc(25% - 8px)' },
                          maxWidth: { xs: 'calc(50% - 8px)', sm: 'calc(50% - 8px)', md: 'calc(25% - 8px)' },
                        }}
                      >
                        <Skeleton 
                          variant="rectangular" 
                          height={50} 
                          sx={{ borderRadius: '12px' }} 
                          animation="wave" 
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              )}
            </Box>
          ) : (
            <Box className="messages-list">
              {messages.map((message) => (
                <MessageItem
                  key={message.id}
                  message={message}
                  renderFormattedDescription={renderFormattedDescription}
                  renderResult={renderResult}
                />
              ))}
              
              {/* Display followup questions after the last bot message */}
              {messages.length > 0 && 
               messages[messages.length - 1]?.sender === 'bot' && 
               !messages[messages.length - 1]?.isError && (
               <Box sx={{ mt: 2, mb: 2, px: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#5f6368',
                    mb: 2,
                    fontSize: '14px',
                    fontWeight: 500,
                    ...( !followupQuestions?.length && {
                      background: 'linear-gradient(90deg, #5f6368 20%, #cac8c8ff 40%, #5f6368 60%)',
                      backgroundSize: '200% 100%',
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      animation: 'shimmerText 2s infinite linear',
                      '@keyframes shimmerText': {
                        '0%': { backgroundPosition: '200% 0' },
                        '100%': { backgroundPosition: '-200% 0' },
                      },
                    })
                  }}
                >
                  Suggested follow-up questions:
                </Typography>

                {followupQuestions && followupQuestions.length > 0 && (
                  <Box
                    className="suggested-prompts"
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    {followupQuestions.map((question, index) => (
                      <QuestionPromptCard
                        key={index}
                        prompt={question}
                        onInputChange={(text) => {
                          if (window.__chatbotSetInput) {
                            window.__chatbotSetInput(text);
                          }
                        }}
                        padding="12px 16px"
                        clearFollowupOnClick={true}
                        queryType={queryType}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              )}
              {isLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: '#00A3E0',
                      width: 36,
                      height: 36,
                      mr: 2,
                    }}
                  >
                    <BotIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Paper
                    className="loading-message-paper"
                    sx={{
                      padding: '12px 16px',
                      backgroundColor: '#F8F8F8',
                      borderRadius: '16px 16px 16px 4px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      minWidth: '200px',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'grey',
                        background: 'linear-gradient(90deg, #5f6368 20%, #e1dfdfff 40%, #5f6368 60%)',
                        backgroundSize: '200% 100%',
                        backgroundClip: 'text',
                        textFillColor: 'transparent',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        animation: 'slideShimmer 2s infinite linear',
                        '@keyframes slideShimmer': {
                          '0%': { backgroundPosition: '200% 0' },
                          '100%': { backgroundPosition: '-200% 0' },
                        },
                      }}
                    >
                      {loadingMessages[currentLoadingMessageIndex]}
                    </Typography>
                  </Paper>


                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        {/* Input Area - Isolated in memoized component to prevent re-renders when typing */}
        <InputArea
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          isConversationLimitReached={isConversationLimitReached}
          userMessageCount={userMessageCount}
          queryType={queryType}
          setQueryType={setQueryType}
          hideDataExplorationAgent={HIDE_DATA_EXPLORATION_AGENT}
        />
      </Box>
      
      {/* Chart Edit Dialog */}
      <Dialog
        open={alertDialog.open}
        onClose={handleCloseAlertDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #005587 0%, #00A3E0 100%)', color: '#fff', fontWeight: 700 }}>
          {alertDialog.alert?.title || 'Alert Details'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pb: 2 }}>
          <Typography sx={{ color: '#4A4A4A', mb: 2, fontSize: '15px', lineHeight: 1.7 }}>
            {alertDialog.alert?.summary || 'No summary available for this alert.'}
          </Typography>

          {alertDialog.alert?.details?.length > 0 && (
            <TableContainer sx={{ border: '1px solid #e8eaed', borderRadius: '8px', boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {(alertDialog.alert?.columns || ALERT_DETAIL_COLUMNS).map((columnName) => (
                      <TableCell key={columnName} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {formatColumnName(columnName)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(alertDialog.alert?.details || []).map((row, idx) => (
                    <TableRow key={`${row.notice_id}-${idx}`} hover>
                      {(alertDialog.alert?.columns || ALERT_DETAIL_COLUMNS).map((columnName) => (
                        <TableCell key={columnName} sx={{ whiteSpace: 'nowrap' }}>
                          {columnName === 'award_url' && row[columnName] && row[columnName] !== 'NULL' ? (
                            <a href={row[columnName]} target="_blank" rel="noreferrer">Link</a>
                          ) : (
                            row[columnName] === 'NULL' || !row[columnName] ? '-' : row[columnName]
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {alertDialog.alert?.details?.length === 0 && (
            <Typography sx={{ color: '#6F6F6F', fontSize: '14px', mt: 1 }}>
              No detailed records available for this alert.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseAlertDialog} variant="contained" sx={{ backgroundColor: '#005587', '&:hover': { backgroundColor: '#00456e' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Chart Edit Dialog */}
      <Dialog 
        open={chartEditDialog.open} 
        onClose={handleChartEditClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#00A3E0', color: '#fff', fontWeight: 600 }}>
          Edit Chart Configuration
        </DialogTitle>
        <DialogContent sx={{ mt: 2, paddingTop: '24px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Chart Type Selection */}
            <FormControl fullWidth required>
              <InputLabel>Chart Type</InputLabel>
              <Select
                value={chartEditDialog.selectedChartType || 'bar'}
                onChange={(e) => setChartEditDialog(prev => ({ ...prev, selectedChartType: e.target.value }))}
                label="Chart Type"
              >
                <MenuItem value="line">Line</MenuItem>
                <MenuItem value="area">Area</MenuItem>
                <MenuItem value="bar">Bar</MenuItem>
                <MenuItem value="scatter">Scatter</MenuItem>
                <MenuItem value="pie">Pie</MenuItem>
                <MenuItem value="radialbar">Radial Bar</MenuItem>
              </Select>
              <FormHelperText>Select the chart visualization type</FormHelperText>
            </FormControl>

            {/* Conditional Fields based on Chart Type */}
            {(chartEditDialog.selectedChartType === 'pie' || chartEditDialog.selectedChartType === 'radialbar') ? (
              <>
                {/* Legend Selection (for labels in pie/radialbar) */}
                <FormControl fullWidth required>
                  <InputLabel>Legend (Labels)</InputLabel>
                  <Select
                    value={chartEditDialog.legend || ''}
                    onChange={(e) => setChartEditDialog(prev => ({ ...prev, legend: e.target.value }))}
                    label="Legend (Labels)"
                  >
                    {chartEditDialog.availableColumns
                      .filter(col => col.type !== 'numeric')
                      .map(col => (
                        <MenuItem key={col.name} value={col.name}>
                          {col.name} ({col.type})
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>Select the column for labels (categories)</FormHelperText>
                </FormControl>

                {/* Value Selection (for data in pie/radialbar) */}
                <FormControl fullWidth required>
                  <InputLabel>Value</InputLabel>
                  <Select
                    value={chartEditDialog.value || ''}
                    onChange={(e) => setChartEditDialog(prev => ({ ...prev, value: e.target.value }))}
                    label="Value"
                  >
                    {chartEditDialog.availableColumns
                      .filter(col => col.type === 'numeric')
                      .map(col => (
                        <MenuItem key={col.name} value={col.name}>
                          {col.name} ({col.type})
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>Select the numeric column for values</FormHelperText>
                </FormControl>
              </>
            ) : (
              <>
                {/* X-Axis Selection */}
                <FormControl fullWidth required>
                  <InputLabel>X-Axis</InputLabel>
                  <Select
                    value={chartEditDialog.xAxis}
                    onChange={(e) => setChartEditDialog(prev => ({ ...prev, xAxis: e.target.value }))}
                    label="X-Axis"
                  >
                    {chartEditDialog.availableColumns
                      .filter(col => col.type !== 'numeric')
                      .map(col => (
                        <MenuItem key={col.name} value={col.name}>
                          {col.name} ({col.type})
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>Select the column for X-axis</FormHelperText>
                </FormControl>

                {/* Y-Axis Selection - Multiselect */}
                <FormControl fullWidth required>
                  <InputLabel>Y-Axis</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(chartEditDialog.yAxis) ? chartEditDialog.yAxis : (chartEditDialog.yAxis ? [chartEditDialog.yAxis] : [])}
                    onChange={(e) => {
                      const selectedYAxis = e.target.value;
                      const isMultiple = Array.isArray(selectedYAxis) && selectedYAxis.length > 1;
                      setChartEditDialog(prev => ({ 
                        ...prev, 
                        yAxis: selectedYAxis,
                        // If multiple Y-axis selected, set legend to 'none' and disable it
                        legend: isMultiple ? 'none' : prev.legend
                      }));
                    }}
                    label="Y-Axis"
                    renderValue={(selected) => {
                      if (Array.isArray(selected) && selected.length > 0) {
                        return selected.join(', ');
                      }
                      return '';
                    }}
                  >
                    {chartEditDialog.availableColumns
                      .filter(col => col.type === 'numeric')
                      .map(col => (
                        <MenuItem key={col.name} value={col.name}>
                          {col.name} ({col.type})
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>
                    {Array.isArray(chartEditDialog.yAxis) && chartEditDialog.yAxis.length > 1
                      ? 'Multiple selections will be used as legend series'
                      : 'Select one or more numeric columns for Y-axis'}
                  </FormHelperText>
                </FormControl>

                {/* Legend Selection */}
                <FormControl fullWidth>
                  <InputLabel>Legend (Optional)</InputLabel>
                  <Select
                    value={chartEditDialog.legend || 'none'}
                    onChange={(e) => setChartEditDialog(prev => ({ ...prev, legend: e.target.value === 'none' ? '' : e.target.value }))}
                    label="Legend (Optional)"
                    disabled={Array.isArray(chartEditDialog.yAxis) && chartEditDialog.yAxis.length > 1}
                  >
                    <MenuItem value="none">None</MenuItem>
                    {chartEditDialog.availableColumns
                      .filter(col => col.type !== 'numeric' && col.name !== chartEditDialog.xAxis)
                      .map(col => (
                        <MenuItem key={col.name} value={col.name}>
                          {col.name} ({col.type})
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>
                    {Array.isArray(chartEditDialog.yAxis) && chartEditDialog.yAxis.length > 1
                      ? 'Legend is automatically set to None when multiple Y-axis values are selected'
                      : 'Select a column for grouping (optional)'}
                  </FormHelperText>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleChartEditClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleChartEditApply} 
            variant="contained"
            sx={{ backgroundColor: '#00A3E0', '&:hover': { backgroundColor: '#005587' } }}
            disabled={
              (chartEditDialog.selectedChartType === 'pie' || chartEditDialog.selectedChartType === 'radialbar')
                ? (!chartEditDialog.legend || !chartEditDialog.value)
                : (!chartEditDialog.xAxis || (Array.isArray(chartEditDialog.yAxis) ? chartEditDialog.yAxis.length === 0 : !chartEditDialog.yAxis))
            }
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatBot;
